import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenantConfig } from '../contexts/TenantConfigContext';
import {
  Upload, BarChart3, Users, TrendingUp, LogOut, FileText,
  Download, Loader2, Trophy, ChevronDown, ChevronRight,
  Zap, AlertTriangle, RefreshCw, Fuel, Clock, ChevronLeft,
  UserPlus, Trash2, Mail, Shield, Activity, HelpCircle, X,
  Palette, Check, Eye, GraduationCap, BookOpen, Target,
  Sparkles, Gauge, Award, ShieldAlert
} from 'lucide-react';
import SelfAudit from './SelfAudit';

const API = process.env.REACT_APP_BACKEND_URL;

/*
 * Anderson College — Campus Connect Brand
 * Navy #1E426C | Navy Deep #1D2F47 | Crimson #A21D23 | Crimson Dark #7A1319
 * Page Tint #EEF1F6 | Surface #FFFFFF | Border #DDE3EC
 * Text #2F2F2F | Text Secondary #5B6572 | Muted #8B95A1
 * Fonts: Jost (headings/KPI), Source Sans 3 (body)
 */

/* ─── Token Meter ─── */
const TokenMeter = ({ usage }) => {
  if (!usage) return null;
  const pct = usage.pct_used || 0;
  const barColor = pct >= 90 ? '#A21D23' : pct >= 70 ? '#D8434B' : '#1E426C';
  const textColor = pct >= 90 ? '#A21D23' : pct >= 70 ? '#D8434B' : '#1D2F47';
  const activity = usage.recent_activity || [];
  return (
    <div className="bg-white rounded-lg p-5" style={{ border: '1px solid #DDE3EC' }} data-testid="token-meter">
      <div className="flex items-center justify-between mb-2.5">
        <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.14em', textTransform: 'uppercase' }}>Token Usage</span>
        <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '18px', fontWeight: 600, color: textColor }}>{usage.tokens_used.toLocaleString()} / {usage.token_cap.toLocaleString()}</span>
      </div>
      <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#EEF1F6' }}>
        <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: barColor, transition: 'width 220ms ease' }} />
      </div>
      <div className="flex justify-between mt-2">
        <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1' }}>{usage.tokens_remaining.toLocaleString()} remaining</span>
        <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1' }}>{pct}%</span>
      </div>
      {activity.length > 0 && (
        <div className="mt-4 pt-3" style={{ borderTop: '1px solid #DDE3EC' }}>
          <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '8px' }}>Recent</p>
          <div className="space-y-1.5 max-h-28 overflow-y-auto">
            {activity.slice(0, 5).map((log, i) => (
              <div key={i} className="flex items-center justify-between">
                <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', color: '#5B6572' }} className="truncate max-w-[150px]">{log.operation?.replace('self_audit_analyze:', 'Analyze ').replace('whisper_transcribe:', 'Transcribe ').replace('vision_ocr:', 'OCR ').replace('batch_report:', 'Batch ')}</span>
                <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1D2F47' }}>{log.tokens?.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Grade Colors (Anderson Brand) ─── */
const gradeColors = {
  A: { bg: '#EEF1F6', text: '#1E426C', border: '#1E426C' },
  B: { bg: '#EEF1F6', text: '#1E426C', border: '#DDE3EC' },
  C: { bg: '#FEF3C7', text: '#92400E', border: '#FDE68A' },
  D: { bg: '#FEE2E2', text: '#A21D23', border: '#FECACA' },
  F: { bg: '#A21D23', text: '#FFFFFF', border: '#A21D23' },
};

const GradeBadge = ({ grade }) => {
  const g = gradeColors[grade] || gradeColors.C;
  return (
    <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 700, color: g.text, backgroundColor: g.bg, border: `1px solid ${g.border}`, padding: '2px 10px', borderRadius: '999px' }}>{grade}</span>
  );
};

const ScoreBar = ({ label, value, max = 10 }) => {
  const pct = Math.min((value / max) * 100, 100);
  const barColor = pct >= 70 ? '#1E426C' : pct >= 50 ? '#D8434B' : '#A21D23';
  return (
    <div className="flex items-center gap-2">
      <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#5B6572', width: '96px', textAlign: 'right' }} className="truncate">{label}</span>
      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#EEF1F6' }}>
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: barColor }} />
      </div>
      <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 600, color: '#1D2F47', width: '32px' }}>{typeof value === 'number' ? value.toFixed(1) : value}</span>
    </div>
  );
};

/* ─── Staff Comparison Tab ─── */
const StaffComparisonView = ({ onDownloadPdf }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [roleFilter, setRoleFilter] = useState('all');
  const [roleCriteria, setRoleCriteria] = useState(null);

  const fetchComparison = useCallback(async () => {
    setLoading(true);
    try {
      const [compRes, critRes] = await Promise.all([
        fetch(`${API}/api/self-audit/staff-comparison`, { credentials: 'include' }),
        fetch(`${API}/api/self-audit/role-criteria`, { credentials: 'include' }),
      ]);
      if (compRes.ok) setData(await compRes.json());
      if (critRes.ok) setRoleCriteria(await critRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchComparison(); }, [fetchComparison]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin" style={{ color: '#1E426C' }} /></div>;
  if (!data || !data.advisors?.length) return (
    <div className="text-center py-16 bg-white rounded-lg" style={{ border: '1px solid #DDE3EC' }}>
      <Users size={32} className="mx-auto mb-3" style={{ color: '#DDE3EC' }} />
      <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '18px', fontWeight: 600, color: '#1D2F47' }}>No analyzed staff data yet</p>
      <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', color: '#8B95A1', marginTop: '4px' }}>Upload and analyze call recordings to see staff comparisons</p>
    </div>
  );

  const roleLabels = { admissions_advisor: 'Admissions Advisor', financial_aid_officer: 'Financial Aid Officer', qualifiers: 'Qualifiers', student_services: 'Student Services' };
  const filteredAdvisors = roleFilter === 'all' ? data.advisors : data.advisors.filter(a => a.staff_role === roleFilter);
  const rolesPresent = data.roles_present || [];

  return (
    <div className="space-y-5">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { value: filteredAdvisors.length, label: 'TOTAL STAFF' },
          { value: filteredAdvisors.reduce((s, a) => s + a.call_count, 0), label: 'CALLS ANALYZED' },
          { value: filteredAdvisors[0]?.name || '-', label: 'TOP PERFORMER' },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-lg p-5" style={{ border: '1px solid #DDE3EC' }}>
            <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '40px', fontWeight: 700, color: '#1D2F47', lineHeight: 1.05 }}>{s.value}</p>
            <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.14em', textTransform: 'uppercase', marginTop: '4px' }}>{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        {/* Role Filter */}
        <div className="flex items-center gap-2">
          <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Filter by role:</span>
          <button onClick={() => setRoleFilter('all')} data-testid="role-filter-all"
            style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: roleFilter === 'all' ? 700 : 500, padding: '4px 12px', borderRadius: '999px', backgroundColor: roleFilter === 'all' ? '#1E426C' : '#EEF1F6', color: roleFilter === 'all' ? 'white' : '#5B6572', border: roleFilter === 'all' ? 'none' : '1px solid #DDE3EC' }}>
            All ({data.advisors.length})
          </button>
          {rolesPresent.map(r => (
            <button key={r} onClick={() => setRoleFilter(r)} data-testid={`role-filter-${r}`}
              style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: roleFilter === r ? 700 : 500, padding: '4px 12px', borderRadius: '999px', backgroundColor: roleFilter === r ? '#1E426C' : '#EEF1F6', color: roleFilter === r ? 'white' : '#5B6572', border: roleFilter === r ? 'none' : '1px solid #DDE3EC' }}>
              {roleLabels[r] || r} ({data.advisors.filter(a => a.staff_role === r).length})
            </button>
          ))}
        </div>
        <button onClick={onDownloadPdf} style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', backgroundColor: '#A21D23', color: 'white', height: '38px', padding: '0 20px', borderRadius: '3px', display: 'flex', alignItems: 'center', gap: '6px' }} className="hover:opacity-90 transition-opacity" data-testid="download-comparison-pdf">
          <Download size={13} /> Export PDF
        </button>
      </div>

      {/* Leaderboard */}
      <div className="bg-white rounded-lg overflow-hidden" style={{ border: '1px solid #DDE3EC' }} data-testid="staff-leaderboard">
        <div className="px-5 py-4" style={{ borderBottom: '1px solid #DDE3EC' }}>
          <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '18px', fontWeight: 600, color: '#1D2F47' }} className="flex items-center gap-2">
            <Trophy size={16} style={{ color: '#A21D23' }} /> Staff Leaderboard
          </h3>
        </div>
        <div>
          {filteredAdvisors.map((advisor) => (
            <div key={advisor.name} style={{ borderBottom: '1px solid #EEF1F6' }} data-testid={`advisor-row-${advisor.name}`}>
              <div className="px-5 py-4 flex items-center gap-4 cursor-pointer hover:bg-[#EEF1F6]/50 transition-colors" onClick={() => setExpanded(expanded === advisor.name ? null : advisor.name)}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: advisor.rank === 1 ? '#A21D23' : '#EEF1F6', color: advisor.rank === 1 ? 'white' : '#1D2F47', fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 700 }}>
                  {advisor.rank}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47' }}>{advisor.name}</p>
                    {advisor.staff_role_label && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#EEF1F6', color: '#1E426C', border: '1px solid #DDE3EC' }} data-testid={`role-badge-${advisor.name}`}>
                        {advisor.staff_role_label}
                      </span>
                    )}
                  </div>
                  <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', color: '#8B95A1' }}>{advisor.call_count} call{advisor.call_count !== 1 ? 's' : ''} analyzed</p>
                </div>
                <GradeBadge grade={advisor.grade} />
                <div className="text-right mr-2">
                  <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '18px', fontWeight: 600, color: '#1D2F47' }}>{advisor.avg_scores?.overall?.toFixed(1)}/10</p>
                  <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1' }}>Overall</p>
                </div>
                <div className="text-right mr-2">
                  <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '18px', fontWeight: 600, color: '#A21D23' }}>{(advisor.avg_scores?.composite * 100)?.toFixed(0)}%</p>
                  <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1' }}>Audit</p>
                </div>
                {expanded === advisor.name ? <ChevronDown size={16} style={{ color: '#8B95A1' }} /> : <ChevronRight size={16} style={{ color: '#8B95A1' }} />}
              </div>
              {expanded === advisor.name && (
                <div className="px-5 pb-5 pt-0">
                  <div className="rounded-lg p-4 grid grid-cols-2 gap-x-8 gap-y-1" style={{ backgroundColor: '#EEF1F6' }}>
                    {(() => {
                      const role = advisor.staff_role || 'admissions_advisor';
                      const dimLabels = roleCriteria?.dimension_labels?.[role] || { tone_empathy: 'Tone & Empathy', information_accuracy: 'Info Accuracy', compliance: 'Compliance', consultative_skill: 'Consultative', closing_ability: 'Closing', persistence: 'Persistence' };
                      const qLabels = roleCriteria?.question_labels?.[role] || { q1: 'Q1', q2: 'Q2', q3: 'Q3', q4: 'Q4', q5: 'Q5' };
                      return (
                        <>
                          {Object.entries(dimLabels).map(([key, label]) => (
                            <ScoreBar key={key} label={label} value={advisor.avg_scores?.[key] || 0} />
                          ))}
                          <div className="col-span-2 mt-3 pt-3" style={{ borderTop: '1px solid #DDE3EC' }}>
                            <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '6px' }}>Audit Questions (avg)</p>
                            <div className="grid grid-cols-5 gap-3">
                              {['q1', 'q2', 'q3', 'q4', 'q5'].map((q) => {
                                const val = advisor.avg_scores?.[q] || 0;
                                return (
                                  <div key={q} className="text-center" title={qLabels[q] || q}>
                                    <div style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 700, color: val >= 0.7 ? '#1E426C' : val >= 0.4 ? '#D8434B' : '#A21D23' }}>
                                      {(val * 100).toFixed(0)}%
                                    </div>
                                    <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '11px', color: '#8B95A1' }} className="truncate" title={qLabels[q]}>{qLabels[q] || q.toUpperCase()}</div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  {advisor.trend?.length > 0 && (
                    <div className="rounded-lg p-4 mt-3" style={{ backgroundColor: '#FAFBFC', border: '1px solid #EEF1F6' }} data-testid={`advisor-trend-${advisor.name}`}>
                      <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>
                        Progress Over Time ({advisor.trend.length} data point{advisor.trend.length !== 1 ? 's' : ''})
                      </p>
                      <div className="flex items-end gap-1" style={{ height: '90px' }}>
                        {advisor.trend.map((t, i) => {
                          const maxVal = Math.max(...advisor.trend.map(d => d.avg_overall), 1);
                          const pct = (t.avg_overall / maxVal) * 100;
                          return (
                            <div key={i} className="flex-1 flex flex-col items-center justify-end group relative" style={{ height: '100%' }}>
                              <div className="absolute -top-7 hidden group-hover:block text-white px-2 py-1 rounded whitespace-nowrap z-10" style={{ backgroundColor: '#1D2F47', fontSize: '11px', fontFamily: 'Source Sans 3, sans-serif' }}>
                                {t.date}: {t.avg_overall}/10 ({t.count} call{t.count !== 1 ? 's' : ''})
                              </div>
                              <div className="w-full rounded-t-sm" style={{ height: `${pct}%`, backgroundColor: '#A21D23', minHeight: '2px', transition: 'height 300ms ease' }} />
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex justify-between mt-2">
                        <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '11px', color: '#8B95A1' }}>{advisor.trend[0]?.date}</span>
                        <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '11px', color: '#8B95A1' }}>{advisor.trend[advisor.trend.length - 1]?.date}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};


/* ─── Role Performance Dashboard ─── */
const ROLE_COLORS = {
  admissions_advisor: '#1E426C',
  financial_aid_officer: '#A21D23',
  qualifiers: '#2980B9',
  student_services: '#16a34a',
};

const RolePerformanceView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/self-audit/role-performance`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin" style={{ color: '#1E426C' }} /></div>;
  if (!data) return null;

  const summaries = data.summaries || [];
  const withCalls = summaries.filter(s => s.call_count > 0);
  const detail = selectedRole ? summaries.find(s => s.role === selectedRole) : null;

  const downloadPdf = async (role) => {
    try {
      const url = role ? `${API}/api/self-audit/pdf/role-performance?role=${role}` : `${API}/api/self-audit/pdf/role-performance`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `role_performance${role ? '_' + role : ''}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
      }
    } catch (e) { console.error(e); }
  };

  return (
    <div className="space-y-5" data-testid="role-performance">
      {/* Header with PDF download */}
      {withCalls.length > 0 && (
        <div className="flex items-center justify-between">
          <div>
            <h2 style={{ fontFamily: 'Jost, sans-serif', fontSize: '18px', fontWeight: 600, color: '#1D2F47' }}>Role Performance</h2>
            <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', color: '#8B95A1' }}>Aggregate scores across all staff roles</p>
          </div>
          <div className="flex items-center gap-2">
            {selectedRole && (
              <button onClick={() => downloadPdf(selectedRole)} data-testid="download-role-pdf-filtered"
                style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', backgroundColor: '#EEF1F6', color: '#1E426C', height: '36px', padding: '0 14px', borderRadius: '3px', display: 'flex', alignItems: 'center', gap: '5px', border: '1px solid #DDE3EC' }}>
                <Download size={12} /> {summaries.find(s => s.role === selectedRole)?.role_label || 'Role'} PDF
              </button>
            )}
            <button onClick={() => downloadPdf(null)} data-testid="download-role-pdf-all"
              style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', backgroundColor: '#A21D23', color: 'white', height: '36px', padding: '0 14px', borderRadius: '3px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Download size={12} /> All Roles PDF
            </button>
          </div>
        </div>
      )}

      {/* Role Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {summaries.map(s => {
          const color = ROLE_COLORS[s.role] || '#8B95A1';
          const isActive = selectedRole === s.role;
          return (
            <button key={s.role} onClick={() => setSelectedRole(isActive ? null : s.role)}
              className="text-left rounded-lg p-5 transition-all" data-testid={`role-card-${s.role}`}
              style={{
                border: isActive ? `2px solid ${color}` : '1px solid #DDE3EC',
                backgroundColor: isActive ? `${color}08` : 'white',
                cursor: 'pointer',
              }}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{s.role_label}</span>
              </div>
              {s.call_count > 0 ? (
                <>
                  <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '36px', fontWeight: 700, color: '#1D2F47', lineHeight: 1 }}>{s.avg_overall}</p>
                  <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1', marginTop: '2px' }}>avg score / 10</p>
                  <div className="flex items-center gap-3 mt-3">
                    <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '14px', fontWeight: 600, color }}>Grade {s.grade}</span>
                    <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1' }}>{s.call_count} call{s.call_count !== 1 ? 's' : ''}</span>
                    <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1' }}>{(s.avg_composite * 100).toFixed(0)}% audit</span>
                  </div>
                </>
              ) : (
                <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', color: '#8B95A1', marginTop: '8px' }}>No data yet</p>
              )}
            </button>
          );
        })}
      </div>

      {/* Comparison Bar Chart */}
      {withCalls.length > 0 && (
        <div className="bg-white rounded-lg p-6" style={{ border: '1px solid #DDE3EC' }} data-testid="role-comparison-chart">
          <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47', marginBottom: '16px' }} className="flex items-center gap-2">
            <BarChart3 size={16} style={{ color: '#1E426C' }} /> Score Comparison by Role
          </h3>
          <div className="space-y-4">
            {withCalls.map(s => {
              const color = ROLE_COLORS[s.role] || '#8B95A1';
              const pct = Math.min((s.avg_overall / 10) * 100, 100);
              return (
                <div key={s.role} className="flex items-center gap-4">
                  <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', fontWeight: 600, color: '#2F2F2F', width: '140px', textAlign: 'right' }} className="truncate">{s.role_label}</span>
                  <div className="flex-1 h-6 rounded-full overflow-hidden" style={{ backgroundColor: '#EEF1F6' }}>
                    <div className="h-full rounded-full flex items-center justify-end pr-2 transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }}>
                      <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '11px', fontWeight: 700, color: 'white' }}>{s.avg_overall}/10</span>
                    </div>
                  </div>
                  <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '14px', fontWeight: 700, color, width: '32px' }}>{s.grade}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Trend Chart for Selected Role */}
      {detail && detail.trend.length > 0 && (
        <div className="bg-white rounded-lg p-6" style={{ border: `1px solid #DDE3EC`, borderTop: `3px solid ${ROLE_COLORS[detail.role] || '#1E426C'}` }} data-testid="role-trend-chart">
          <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47', marginBottom: '4px' }}>
            {detail.role_label} — Score Trend
          </h3>
          <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1', marginBottom: '16px' }}>
            Daily average overall score ({detail.trend.length} data point{detail.trend.length !== 1 ? 's' : ''})
          </p>
          <div className="flex items-end gap-1" style={{ height: '120px' }}>
            {detail.trend.map((t, i) => {
              const maxVal = Math.max(...detail.trend.map(d => d.avg_overall), 1);
              const pct = (t.avg_overall / maxVal) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center justify-end group relative" style={{ height: '100%' }}>
                  <div className="absolute -top-7 hidden group-hover:block text-white px-2 py-1 rounded whitespace-nowrap z-10" style={{ backgroundColor: '#1D2F47', fontSize: '11px', fontFamily: 'Source Sans 3, sans-serif' }}>
                    {t.date}: {t.avg_overall}/10 ({t.count} call{t.count !== 1 ? 's' : ''})
                  </div>
                  <div className="w-full rounded-t-sm" style={{ height: `${pct}%`, backgroundColor: ROLE_COLORS[detail.role] || '#1E426C', minHeight: '2px', transition: 'height 300ms ease' }} />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-2">
            <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '11px', color: '#8B95A1' }}>{detail.trend[0]?.date}</span>
            <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '11px', color: '#8B95A1' }}>{detail.trend[detail.trend.length - 1]?.date}</span>
          </div>
        </div>
      )}

      {withCalls.length === 0 && (
        <div className="text-center py-16 bg-white rounded-lg" style={{ border: '1px solid #DDE3EC' }}>
          <BarChart3 size={32} className="mx-auto mb-3" style={{ color: '#DDE3EC' }} />
          <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '18px', fontWeight: 600, color: '#1D2F47' }}>No analyzed data yet</p>
          <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', color: '#8B95A1', marginTop: '4px' }}>Upload and analyze calls with different staff roles to see performance comparisons</p>
        </div>
      )}
    </div>
  );
};


/* ─── Token History Tab ─── */

/* ─── Campus Performance Dashboard ─── */
const CAMPUS_COLORS = {
  'Toronto': '#1E426C', 'North York': '#A21D23', 'Mississauga': '#2980B9', 'Brampton': '#16a34a',
  'Hamilton': '#7C3AED', 'Brantford': '#D97706', 'Kitchener': '#0891B2', 'London': '#BE185D',
  'Windsor': '#4338CA', 'Online': '#64748B', 'Unassigned': '#94A3B8',
};

const CampusPerformanceView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCampus, setSelectedCampus] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/self-audit/campus-performance`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin" style={{ color: '#1E426C' }} /></div>;
  if (!data) return null;

  const summaries = data.summaries || [];
  const withCalls = summaries.filter(s => s.call_count > 0);
  const detail = selectedCampus ? summaries.find(s => s.campus === selectedCampus) : null;

  return (
    <div className="space-y-5" data-testid="campus-performance">
      {/* Header */}
      <div>
        <h2 style={{ fontFamily: 'Jost, sans-serif', fontSize: '18px', fontWeight: 600, color: '#1D2F47' }}>Campus Performance</h2>
        <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', color: '#8B95A1' }}>Aggregate quality scores by campus location</p>
      </div>

      {/* Campus Cards Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {summaries.map(s => {
          const color = CAMPUS_COLORS[s.campus] || '#8B95A1';
          const isActive = selectedCampus === s.campus;
          return (
            <button key={s.campus} onClick={() => setSelectedCampus(isActive ? null : s.campus)}
              className="text-left rounded-lg p-4 transition-all" data-testid={`campus-card-${s.campus}`}
              style={{ border: isActive ? `2px solid ${color}` : '1px solid #DDE3EC', backgroundColor: isActive ? `${color}08` : 'white' }}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '11px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{s.campus}</span>
              </div>
              {s.call_count > 0 ? (
                <>
                  <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '28px', fontWeight: 700, color: '#1D2F47', lineHeight: 1 }}>{s.avg_overall}<span style={{ fontSize: '13px', color: '#8B95A1', fontWeight: 500 }}>/10</span></p>
                  <div className="flex items-center gap-2 mt-2">
                    <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 700, color }}>{s.grade}</span>
                    <span style={{ fontSize: '11px', color: '#8B95A1' }}>{s.call_count} calls</span>
                    <span style={{ fontSize: '11px', color: '#8B95A1' }}>{s.staff_count} staff</span>
                  </div>
                </>
              ) : (
                <p style={{ fontSize: '12px', color: '#8B95A1', marginTop: '6px' }}>No data</p>
              )}
            </button>
          );
        })}
      </div>

      {/* Comparison Bar Chart */}
      {withCalls.length > 0 && (
        <div className="bg-white rounded-lg p-6" style={{ border: '1px solid #DDE3EC' }} data-testid="campus-comparison-chart">
          <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47', marginBottom: '16px' }}>Score Comparison by Campus</h3>
          <div className="space-y-3">
            {withCalls.map(s => {
              const color = CAMPUS_COLORS[s.campus] || '#8B95A1';
              const pct = Math.min((s.avg_overall / 10) * 100, 100);
              return (
                <div key={s.campus} className="flex items-center gap-4">
                  <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', fontWeight: 600, color: '#2F2F2F', width: '100px', textAlign: 'right' }}>{s.campus}</span>
                  <div className="flex-1 h-5 rounded-full overflow-hidden" style={{ backgroundColor: '#EEF1F6' }}>
                    <div className="h-full rounded-full flex items-center justify-end pr-2 transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color, minWidth: pct > 5 ? '40px' : '0' }}>
                      <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '10px', fontWeight: 700, color: 'white' }}>{s.avg_overall}</span>
                    </div>
                  </div>
                  <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 700, color, width: '28px' }}>{s.grade}</span>
                  <span style={{ fontSize: '11px', color: '#8B95A1', width: '60px' }}>{s.call_count} calls</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Selected Campus Detail */}
      {detail && detail.call_count > 0 && (
        <div className="grid grid-cols-2 gap-4">
          {/* Role Breakdown */}
          <div className="bg-white rounded-lg p-5" style={{ border: '1px solid #DDE3EC', borderTop: `3px solid ${CAMPUS_COLORS[detail.campus] || '#1E426C'}` }} data-testid="campus-role-breakdown">
            <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '15px', fontWeight: 600, color: '#1D2F47', marginBottom: '12px' }}>{detail.campus} — Role Breakdown</h3>
            {detail.role_breakdown?.length > 0 ? (
              <div className="space-y-3">
                {detail.role_breakdown.map(rb => (
                  <div key={rb.role} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ backgroundColor: '#FAFBFC' }}>
                    <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', fontWeight: 600, color: '#1D2F47' }}>{rb.role_label}</span>
                    <div className="flex items-center gap-3">
                      <span style={{ fontSize: '11px', color: '#8B95A1' }}>{rb.count} calls</span>
                      <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '14px', fontWeight: 700, color: CAMPUS_COLORS[detail.campus] || '#1E426C' }}>{rb.avg_overall}/10</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p style={{ fontSize: '13px', color: '#8B95A1' }}>No role data</p>}
          </div>

          {/* Trend Chart */}
          {detail.trend?.length > 0 && (
            <div className="bg-white rounded-lg p-5" style={{ border: '1px solid #DDE3EC', borderTop: `3px solid ${CAMPUS_COLORS[detail.campus] || '#1E426C'}` }} data-testid="campus-trend-chart">
              <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '15px', fontWeight: 600, color: '#1D2F47', marginBottom: '4px' }}>{detail.campus} — Score Trend</h3>
              <p style={{ fontSize: '11px', color: '#8B95A1', marginBottom: '12px' }}>{detail.trend.length} data points</p>
              <div className="flex items-end gap-1" style={{ height: '100px' }}>
                {detail.trend.map((t, i) => {
                  const maxVal = Math.max(...detail.trend.map(d => d.avg_overall), 1);
                  const pct = (t.avg_overall / maxVal) * 100;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center justify-end group relative" style={{ height: '100%' }}>
                      <div className="absolute -top-7 hidden group-hover:block text-white px-2 py-1 rounded whitespace-nowrap z-10" style={{ backgroundColor: '#1D2F47', fontSize: '10px' }}>
                        {t.date}: {t.avg_overall}/10 ({t.count} calls)
                      </div>
                      <div className="w-full rounded-t-sm" style={{ height: `${pct}%`, backgroundColor: CAMPUS_COLORS[detail.campus] || '#1E426C', minHeight: '2px', transition: 'height 300ms ease' }} />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2">
                <span style={{ fontSize: '10px', color: '#8B95A1' }}>{detail.trend[0]?.date}</span>
                <span style={{ fontSize: '10px', color: '#8B95A1' }}>{detail.trend[detail.trend.length - 1]?.date}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {withCalls.length === 0 && (
        <div className="text-center py-16 bg-white rounded-lg" style={{ border: '1px solid #DDE3EC' }}>
          <TrendingUp size={28} className="mx-auto mb-3" style={{ color: '#DDE3EC' }} />
          <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47' }}>No campus data yet</p>
          <p style={{ fontSize: '13px', color: '#8B95A1', marginTop: '4px' }}>Map uploads to campuses and analyze them to see campus performance</p>
        </div>
      )}
    </div>
  );
};


const TokenHistoryView = ({ isAdmin }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [members, setMembers] = useState([]);
  const [caps, setCaps] = useState({});
  const [savingEmail, setSavingEmail] = useState(null);
  const [toppingUp, setToppingUp] = useState(false);

  const fetchHistory = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/self-audit/token-history?page=${p}&limit=50`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setData(d); setPage(p); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const fetchMembers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/self-audit/team`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        const list = d.members || [];
        setMembers(list);
        setCaps(Object.fromEntries(list.map(m => [m.email, m.token_cap ?? 0])));
      }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchHistory(1); }, [fetchHistory]);
  useEffect(() => { if (isAdmin) fetchMembers(); }, [isAdmin, fetchMembers]);

  const saveCap = async (email) => {
    setSavingEmail(email);
    try {
      const res = await fetch(`${API}/api/self-audit/tokens/set-cap`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token_cap: Number(caps[email]) }),
      });
      if (res.ok) { await fetchMembers(); await fetchHistory(1); }
      else { const e = await res.json().catch(() => ({})); alert(e.detail || 'Failed to update cap'); }
    } catch (e) { console.error(e); }
    finally { setSavingEmail(null); }
  };

  const topUpAll = async () => {
    if (!window.confirm('Top up (reset) consumed tokens for ALL team members back to their full cap? This clears the usage history counters.')) return;
    setToppingUp(true);
    try {
      const res = await fetch(`${API}/api/self-audit/tokens/topup`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      });
      if (res.ok) { await fetchMembers(); await fetchHistory(1); }
      else { const e = await res.json().catch(() => ({})); alert(e.detail || 'Top up failed'); }
    } catch (e) { console.error(e); }
    finally { setToppingUp(false); }
  };

  if (loading && !data) return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin" style={{ color: '#1E426C' }} /></div>;

  const logs = data?.logs || [];
  const daily = data?.daily_usage || [];
  const maxDailyTokens = Math.max(...daily.map(d => d.tokens), 1);

  const opLabel = (op) => {
    if (!op) return 'Unknown';
    if (op.startsWith('self_audit_analyze:')) return `Analysis (${op.split(':')[1]})`;
    if (op.startsWith('whisper_transcribe:')) return `Transcription (${op.split(':')[1]})`;
    if (op.startsWith('vision_ocr:')) return `OCR Scan (${op.split(':')[1]})`;
    if (op.startsWith('batch_report:')) return `Batch Report (${op.split(':')[1]})`;
    return op;
  };

  const opDot = (op) => {
    if (!op) return '#8B95A1';
    if (op.startsWith('self_audit_analyze')) return '#1E426C';
    if (op.startsWith('whisper_transcribe')) return '#1D8348';
    if (op.startsWith('vision_ocr')) return '#2980B9';
    if (op.startsWith('batch_report')) return '#A21D23';
    return '#8B95A1';
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-5" data-testid="token-history-view">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { value: (data?.tokens_used || 0).toLocaleString(), label: 'TOTAL USED' },
          { value: (data?.token_cap || 0).toLocaleString(), label: 'TOKEN CAP' },
          { value: data?.total || 0, label: 'TOTAL OPERATIONS' },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-lg p-5" style={{ border: '1px solid #DDE3EC' }}>
            <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '40px', fontWeight: 700, color: '#1D2F47', lineHeight: 1.05 }}>{s.value}</p>
            <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.14em', textTransform: 'uppercase', marginTop: '4px' }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Admin: Team Token Caps */}
      {isAdmin && (
        <div className="bg-white rounded-lg p-5" style={{ border: '1px solid #DDE3EC' }} data-testid="token-cap-admin">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Shield size={16} style={{ color: '#A21D23' }} />
              <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47' }}>Team Token Caps</h3>
            </div>
            <button onClick={topUpAll} disabled={toppingUp} className="flex items-center gap-1.5 disabled:opacity-50 transition-opacity hover:opacity-90" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', fontWeight: 700, color: 'white', backgroundColor: '#1E426C', padding: '8px 14px', borderRadius: '5px', border: 'none', cursor: toppingUp ? 'wait' : 'pointer' }} data-testid="token-topup-all">
              {toppingUp ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Top up all (reset usage)
            </button>
          </div>
          <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', color: '#8B95A1', marginBottom: '14px' }}>Set each member's token allowance. "Top up" clears consumed usage so everyone returns to their full cap.</p>
          <div className="overflow-x-auto">
            <table className="w-full" style={{ fontFamily: 'Source Sans 3, sans-serif' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #DDE3EC' }}>
                  <th className="text-left py-2" style={{ fontSize: '11px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Member</th>
                  <th className="text-left py-2" style={{ fontSize: '11px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Token Cap</th>
                  <th className="text-right py-2"></th>
                </tr>
              </thead>
              <tbody>
                {members.length === 0 ? (
                  <tr><td colSpan={3} className="py-4" style={{ fontSize: '13px', color: '#8B95A1' }}>No team members found.</td></tr>
                ) : members.map((m) => (
                  <tr key={m.email} style={{ borderBottom: '1px solid #EEF1F6' }} data-testid={`cap-row-${m.email}`}>
                    <td className="py-2.5">
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#2F2F2F' }}>{m.name || m.email}</div>
                      <div style={{ fontSize: '12px', color: '#8B95A1' }}>{m.email}{m.team_role ? ` · ${m.team_role}` : ''}</div>
                    </td>
                    <td className="py-2.5">
                      <input type="number" min="0" step="10000" value={caps[m.email] ?? ''} onChange={e => setCaps(c => ({ ...c, [m.email]: e.target.value }))}
                        className="outline-none" style={{ width: '140px', fontSize: '14px', color: '#2F2F2F', padding: '7px 10px', border: '1px solid #DDE3EC', borderRadius: '5px', background: '#F7F9FC' }}
                        data-testid={`cap-input-${m.email}`} />
                    </td>
                    <td className="py-2.5 text-right">
                      <button onClick={() => saveCap(m.email)} disabled={savingEmail === m.email} className="disabled:opacity-50 hover:opacity-90 transition-opacity" style={{ fontSize: '12px', fontWeight: 700, color: 'white', backgroundColor: '#A21D23', padding: '7px 14px', borderRadius: '5px', border: 'none', cursor: 'pointer' }} data-testid={`cap-save-${m.email}`}>
                        {savingEmail === m.email ? 'Saving…' : 'Save'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {daily.length > 0 && (
        <div className="bg-white rounded-lg p-5" style={{ border: '1px solid #DDE3EC' }} data-testid="daily-usage-chart">
          <h3 style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '16px' }} className="flex items-center gap-2">
            <BarChart3 size={13} style={{ color: '#1E426C' }} /> Daily Token Usage (Last 30 Days)
          </h3>
          <div className="flex items-end gap-1 h-28">
            {daily.slice().reverse().map((d, i) => {
              const pct = Math.max((d.tokens / maxDailyTokens) * 100, 3);
              return (
                <div key={i} className="flex-1 group relative flex flex-col items-center justify-end">
                  <div className="absolute -top-7 hidden group-hover:block text-white px-2 py-1 rounded whitespace-nowrap z-10" style={{ backgroundColor: '#1D2F47', fontSize: '11px', fontFamily: 'Source Sans 3, sans-serif' }}>
                    {d.date}: {d.tokens.toLocaleString()}
                  </div>
                  <div className="w-full rounded-t-sm" style={{ height: `${pct}%`, backgroundColor: '#1E426C', transition: 'background-color 220ms ease' }} onMouseEnter={e => e.target.style.backgroundColor = '#A21D23'} onMouseLeave={e => e.target.style.backgroundColor = '#1E426C'} />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-2">
            <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1' }}>{daily[daily.length - 1]?.date}</span>
            <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1' }}>{daily[0]?.date}</span>
          </div>
        </div>
      )}

      {/* Operations Log */}
      <div className="bg-white rounded-lg overflow-hidden" style={{ border: '1px solid #DDE3EC' }} data-testid="token-log-table">
        <div className="px-5 py-3.5 flex items-center justify-between" style={{ borderBottom: '1px solid #DDE3EC', backgroundColor: '#FAFBFC' }}>
          <h3 style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.12em', textTransform: 'uppercase' }} className="flex items-center gap-2">
            <Clock size={13} style={{ color: '#1E426C' }} /> Operation Log
          </h3>
          <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1' }}>
            Page {page} of {data?.pages || 1} ({data?.total || 0} total)
          </span>
        </div>

        {logs.length === 0 ? (
          <div className="p-12 text-center">
            <Clock size={28} className="mx-auto mb-2" style={{ color: '#DDE3EC' }} />
            <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '18px', fontWeight: 600, color: '#1D2F47' }}>No token activity yet</p>
            <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', color: '#8B95A1', marginTop: '4px' }}>Upload and analyze files to see usage here</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: '1px solid #DDE3EC' }}>
                <th className="text-left px-5 py-2.5" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Operation</th>
                <th className="text-right px-5 py-2.5" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Tokens</th>
                <th className="text-right px-5 py-2.5" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.12em', textTransform: 'uppercase' }}>When</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #EEF1F6' }} className="hover:bg-[#EEF1F6]/40" data-testid={`token-log-row-${i}`}>
                  <td className="px-5 py-3 flex items-center gap-2.5">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: opDot(log.operation) }} />
                    <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', color: '#2F2F2F' }}>{opLabel(log.operation)}</span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47' }}>{(log.tokens || 0).toLocaleString()}</span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', color: '#8B95A1' }}>{fmtDate(log.created_at)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {(data?.pages || 1) > 1 && (
          <div className="px-5 py-3 flex items-center justify-between" style={{ borderTop: '1px solid #DDE3EC' }}>
            <button onClick={() => fetchHistory(page - 1)} disabled={page <= 1} className="flex items-center gap-1 disabled:opacity-30" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', color: '#1E426C' }} data-testid="token-history-prev">
              <ChevronLeft size={14} /> Previous
            </button>
            <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1' }}>Page {page} of {data?.pages}</span>
            <button onClick={() => fetchHistory(page + 1)} disabled={page >= (data?.pages || 1)} className="flex items-center gap-1 disabled:opacity-30" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', color: '#1E426C' }} data-testid="token-history-next">
              Next <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-5 flex-wrap">
        {[
          { color: '#1E426C', label: 'Analysis' },
          { color: '#1D8348', label: 'Transcription' },
          { color: '#2980B9', label: 'OCR Scan' },
          { color: '#A21D23', label: 'Batch Report' },
        ].map(l => (
          <div key={l.label} className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: l.color }} />
            <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#5B6572' }}>{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Team Management Tab ─── */
const TeamManagementView = () => {
  const { user: currentUser } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'analyst' });
  const [inviting, setInviting] = useState(false);

  const fetchTeam = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/self-audit/team`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setMembers(d.members || []); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) return;
    setInviting(true);
    try {
      const res = await fetch(`${API}/api/self-audit/team/invite`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setShowInvite(false);
        setForm({ email: '', password: '', name: '', role: 'analyst' });
        fetchTeam();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to invite user');
      }
    } catch (e) { console.error(e); }
    finally { setInviting(false); }
  };

  const handleRemove = async (email) => {
    if (!window.confirm(`Remove ${email} from the team?`)) return;
    try {
      const res = await fetch(`${API}/api/self-audit/team/${encodeURIComponent(email)}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) fetchTeam();
      else { const err = await res.json().catch(() => ({})); alert(err.detail || 'Remove failed'); }
    } catch (e) { console.error(e); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin" style={{ color: '#1E426C' }} /></div>;

  return (
    <div className="space-y-5" data-testid="team-management-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 style={{ fontFamily: 'Jost, sans-serif', fontSize: '20px', fontWeight: 600, color: '#1D2F47' }}>Team Members</h2>
          <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', color: '#8B95A1' }}>{members.length} member{members.length !== 1 ? 's' : ''} in your organization</p>
        </div>
        <button onClick={() => setShowInvite(v => !v)} style={{ backgroundColor: '#A21D23', color: 'white', height: '38px', padding: '0 20px', borderRadius: '3px', fontFamily: 'Source Sans 3, sans-serif', fontSize: '13.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }} className="hover:opacity-90 transition-opacity" data-testid="invite-user-btn">
          <UserPlus size={14} /> Invite User
        </button>
      </div>

      {/* Invite Form */}
      {showInvite && (
        <form onSubmit={handleInvite} className="bg-white rounded-lg p-6" style={{ border: '1px solid #DDE3EC', borderTop: '3px solid #A21D23' }} data-testid="invite-user-form">
          <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47', marginBottom: '16px' }}>Invite New Team Member</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', fontWeight: 600, color: '#2F2F2F', display: 'block', marginBottom: '4px' }}>Email *</label>
              <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} type="email" required
                className="w-full outline-none" style={{ border: '1px solid #DDE3EC', borderRadius: '6px', padding: '9px 12px', fontSize: '14px', fontFamily: 'Source Sans 3, sans-serif' }}
                onFocus={e => e.target.style.borderColor = '#1E426C'} onBlur={e => e.target.style.borderColor = '#DDE3EC'}
                placeholder="colleague@example.com" data-testid="invite-email" />
            </div>
            <div>
              <label style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', fontWeight: 600, color: '#2F2F2F', display: 'block', marginBottom: '4px' }}>Temporary Password *</label>
              <input value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} type="text" required minLength={6}
                className="w-full outline-none" style={{ border: '1px solid #DDE3EC', borderRadius: '6px', padding: '9px 12px', fontSize: '14px', fontFamily: 'Source Sans 3, sans-serif' }}
                onFocus={e => e.target.style.borderColor = '#1E426C'} onBlur={e => e.target.style.borderColor = '#DDE3EC'}
                placeholder="Min 6 characters" data-testid="invite-password" />
            </div>
            <div>
              <label style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', fontWeight: 600, color: '#2F2F2F', display: 'block', marginBottom: '4px' }}>Name</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full outline-none" style={{ border: '1px solid #DDE3EC', borderRadius: '6px', padding: '9px 12px', fontSize: '14px', fontFamily: 'Source Sans 3, sans-serif' }}
                onFocus={e => e.target.style.borderColor = '#1E426C'} onBlur={e => e.target.style.borderColor = '#DDE3EC'}
                placeholder="Full name" data-testid="invite-name" />
            </div>
            <div>
              <label style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', fontWeight: 600, color: '#2F2F2F', display: 'block', marginBottom: '4px' }}>Role</label>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                className="w-full outline-none bg-white" style={{ border: '1px solid #DDE3EC', borderRadius: '6px', padding: '9px 12px', fontSize: '14px', fontFamily: 'Source Sans 3, sans-serif' }}
                data-testid="invite-role">
                <option value="analyst">Analyst</option>
                <option value="admin">Admin</option>
                <option value="doa">DOA (Training Only)</option>
              </select>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <button type="submit" disabled={inviting} style={{ backgroundColor: '#A21D23', color: 'white', height: '38px', padding: '0 20px', borderRadius: '3px', fontFamily: 'Source Sans 3, sans-serif', fontSize: '13.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }} className="disabled:opacity-50" data-testid="submit-invite">
              {inviting ? <Loader2 size={13} className="animate-spin" /> : <UserPlus size={13} />} Send Invite
            </button>
            <button type="button" onClick={() => setShowInvite(false)} style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', color: '#8B95A1' }}>Cancel</button>
          </div>
        </form>
      )}

      {/* Member List */}
      <div className="bg-white rounded-lg overflow-hidden" style={{ border: '1px solid #DDE3EC' }} data-testid="team-member-list">
        {members.length === 0 ? (
          <div className="p-12 text-center">
            <Users size={28} className="mx-auto mb-2" style={{ color: '#DDE3EC' }} />
            <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '18px', fontWeight: 600, color: '#1D2F47' }}>No team members yet</p>
            <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', color: '#8B95A1', marginTop: '4px' }}>Invite colleagues to collaborate on self-audit analysis</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: '1px solid #DDE3EC', backgroundColor: '#FAFBFC' }}>
                <th className="text-left px-5 py-3" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Member</th>
                <th className="text-left px-5 py-3" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Role</th>
                <th className="text-left px-5 py-3" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Tokens</th>
                <th className="text-left px-5 py-3" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Joined</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {members.map(m => {
                const isYou = m.email === currentUser?.email;
                const role = m.team_role || (m.invited_by ? 'analyst' : 'admin');
                return (
                  <tr key={m._id} style={{ borderBottom: '1px solid #EEF1F6' }} className="hover:bg-[#EEF1F6]/40" data-testid={`team-row-${m.email}`}>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: isYou ? '#1E426C' : '#EEF1F6', color: isYou ? 'white' : '#1D2F47', fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 700 }}>
                          {(m.name || m.email)[0].toUpperCase()}
                        </div>
                        <div>
                          <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1D2F47' }}>
                            {m.name || 'Unnamed'} {isYou && <span style={{ fontSize: '11px', color: '#8B95A1', fontWeight: 400 }}>(you)</span>}
                          </p>
                          <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1' }}>{m.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <span style={{
                        fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: '0.08em',
                        padding: '2px 10px', borderRadius: '999px',
                        backgroundColor: role === 'admin' ? '#1E426C' : role === 'doa' ? '#2980B9' : '#EEF1F6',
                        color: role === 'admin' || role === 'doa' ? 'white' : '#5B6572',
                      }}>{role}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1D2F47' }}>
                        {(m.tokens_used || 0).toLocaleString()} / {(m.token_cap || 0).toLocaleString()}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', color: '#8B95A1' }}>
                        {m.created_at ? new Date(m.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      {!isYou && (
                        <button onClick={() => handleRemove(m.email)} className="transition-opacity hover:opacity-70" style={{ color: '#A21D23' }} data-testid={`remove-member-${m.email}`}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

/* ─── Activity Log Tab ─── */
const ActivityLogView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const fetchLogs = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/self-audit/activity-log?page=${p}&limit=50`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setData(d); setPage(p); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchLogs(1); }, [fetchLogs]);

  if (loading && !data) return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin" style={{ color: '#1E426C' }} /></div>;

  const logs = data?.logs || [];

  const actionIcons = {
    file_upload: { color: '#1E426C', label: 'File Upload' },
    file_map: { color: '#2980B9', label: 'File Mapped' },
    analysis_complete: { color: '#16a34a', label: 'Analysis Complete' },
    analysis_reset: { color: '#D8434B', label: 'Analysis Reset' },
    analysis_feedback: { color: '#9333EA', label: 'Feedback Submitted' },
    methodology_update: { color: '#A21D23', label: 'Methodology Updated' },
    team_invite: { color: '#0891B2', label: 'Team Invite' },
    team_remove: { color: '#DC2626', label: 'Team Remove' },
    batch_report_delete: { color: '#D97706', label: 'Report Deleted' },
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-5" data-testid="activity-log-view">
      <div>
        <h2 style={{ fontFamily: 'Jost, sans-serif', fontSize: '20px', fontWeight: 600, color: '#1D2F47' }}>Activity Log</h2>
        <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', color: '#8B95A1' }}>All actions across your team — uploads, analyses, settings changes, and team updates</p>
      </div>

      <div className="bg-white rounded-lg overflow-hidden" style={{ border: '1px solid #DDE3EC' }} data-testid="activity-log-table">
        <div className="px-5 py-3.5 flex items-center justify-between" style={{ borderBottom: '1px solid #DDE3EC', backgroundColor: '#FAFBFC' }}>
          <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.12em', textTransform: 'uppercase' }} className="flex items-center gap-2">
            <Activity size={13} style={{ color: '#1E426C' }} /> All Activity
          </span>
          <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1' }}>
            Page {page} of {data?.pages || 1} ({data?.total || 0} events)
          </span>
        </div>

        {logs.length === 0 ? (
          <div className="p-12 text-center">
            <Activity size={28} className="mx-auto mb-2" style={{ color: '#DDE3EC' }} />
            <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '18px', fontWeight: 600, color: '#1D2F47' }}>No activity yet</p>
            <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', color: '#8B95A1', marginTop: '4px' }}>Upload and analyze files to start seeing activity here</p>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: '#EEF1F6' }}>
            {logs.map((log, i) => {
              const info = actionIcons[log.action] || { color: '#8B95A1', label: log.action };
              return (
                <div key={i} className="px-5 py-3.5 flex items-center gap-4 hover:bg-[#EEF1F6]/40 transition-colors" data-testid={`activity-row-${i}`}>
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: info.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', fontWeight: 700, color: info.color }}>{info.label}</span>
                      {log.detail && <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', color: '#2F2F2F' }}>— {log.detail}</span>}
                    </div>
                    <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1' }}>
                      {log.user_name || log.user_email}
                    </span>
                  </div>
                  <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1', flexShrink: 0 }}>{fmtDate(log.created_at)}</span>
                </div>
              );
            })}
          </div>
        )}

        {(data?.pages || 1) > 1 && (
          <div className="px-5 py-3 flex items-center justify-between" style={{ borderTop: '1px solid #DDE3EC' }}>
            <button onClick={() => fetchLogs(page - 1)} disabled={page <= 1} className="flex items-center gap-1 disabled:opacity-30" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', color: '#1E426C' }} data-testid="activity-log-prev">
              <ChevronLeft size={14} /> Previous
            </button>
            <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1' }}>Page {page} of {data?.pages}</span>
            <button onClick={() => fetchLogs(page + 1)} disabled={page >= (data?.pages || 1)} className="flex items-center gap-1 disabled:opacity-30" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', color: '#1E426C' }} data-testid="activity-log-next">
              Next <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Settings & Methodology Tab ─── */
const METHODOLOGY_ROLES = [
  { key: 'role_admissions_advisor', label: 'Admissions Advisor', desc: 'Custom instructions for evaluating admissions advisor calls' },
  { key: 'role_financial_aid_officer', label: 'Financial Aid Officer', desc: 'Custom instructions for evaluating FAO interactions' },
  { key: 'role_qualifiers', label: 'Qualifiers', desc: 'Custom instructions for evaluating first-contact qualifiers' },
  { key: 'role_student_services', label: 'Student Services', desc: 'Custom instructions for evaluating student services staff' },
];

const MethodologySettingsView = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newFocus, setNewFocus] = useState('');
  const [newQuestion, setNewQuestion] = useState('');
  const [expandedRole, setExpandedRole] = useState(null);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/self-audit/methodology-config`, { credentials: 'include' });
      if (res.ok) setConfig(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/self-audit/methodology-config`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      if (res.ok) { setSaved(true); setTimeout(() => setSaved(false), 3000); }
      else { const err = await res.json().catch(() => ({})); alert(err.detail || 'Save failed'); }
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const addFocus = () => {
    if (!newFocus.trim()) return;
    setConfig(c => ({ ...c, scoring_focus: [...(c.scoring_focus || []), newFocus.trim()] }));
    setNewFocus('');
  };

  const removeFocus = (i) => setConfig(c => ({ ...c, scoring_focus: c.scoring_focus.filter((_, idx) => idx !== i) }));

  const addQuestion = () => {
    if (!newQuestion.trim()) return;
    setConfig(c => ({ ...c, additional_questions: [...(c.additional_questions || []), newQuestion.trim()] }));
    setNewQuestion('');
  };

  const removeQuestion = (i) => setConfig(c => ({ ...c, additional_questions: c.additional_questions.filter((_, idx) => idx !== i) }));

  if (loading || !config) return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin" style={{ color: '#1E426C' }} /></div>;

  const inputStyle = { border: '1px solid #DDE3EC', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', fontFamily: 'Source Sans 3, sans-serif', color: '#2F2F2F', width: '100%', outline: 'none' };
  const textareaStyle = { ...inputStyle, minHeight: '80px', resize: 'vertical', lineHeight: '1.5' };
  const labelStyle = { fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', fontWeight: 600, color: '#2F2F2F', display: 'block', marginBottom: '6px' };
  const helpStyle = { fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1', marginTop: '4px' };
  const tagStyle = { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '999px', fontSize: '12px', fontFamily: 'Source Sans 3, sans-serif', fontWeight: 600 };

  return (
    <div className="space-y-6" data-testid="methodology-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 style={{ fontFamily: 'Jost, sans-serif', fontSize: '20px', fontWeight: 600, color: '#1D2F47' }}>Analysis Methodology</h2>
          <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', color: '#8B95A1' }}>
            Customize how the AI evaluates your staff — these settings apply only to your organization
          </p>
        </div>
        <button onClick={handleSave} disabled={saving} style={{ backgroundColor: saved ? '#1E426C' : '#A21D23', color: 'white', height: '38px', padding: '0 24px', borderRadius: '3px', fontFamily: 'Source Sans 3, sans-serif', fontSize: '13.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }} className="hover:opacity-90 transition-all disabled:opacity-50" data-testid="save-methodology-btn">
          {saving ? <Loader2 size={13} className="animate-spin" /> : saved ? <Shield size={13} /> : <Shield size={13} />}
          {saving ? 'Saving...' : saved ? 'Saved' : 'Save Changes'}
        </button>
      </div>

      {/* Custom Instructions */}
      <div className="bg-white rounded-lg p-6" style={{ border: '1px solid #DDE3EC', borderTop: '3px solid #A21D23' }}>
        <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47', marginBottom: '16px' }}>Custom Analysis Instructions</h3>
        <div className="space-y-4">
          <div>
            <label style={labelStyle}>General Instructions for the AI Evaluator</label>
            <textarea value={config.custom_instructions || ''} onChange={e => setConfig(c => ({ ...c, custom_instructions: e.target.value }))}
              style={textareaStyle} placeholder="e.g., 'Our advisors use a consultative approach. Value rapport-building over hard selling. Weight empathy and needs-discovery higher than closing pressure.'" data-testid="custom-instructions" />
            <p style={helpStyle}>These instructions are injected into every analysis for your organization only. Be specific about what matters to your team.</p>
          </div>

          <div>
            <label style={labelStyle}>Industry / Program Context</label>
            <textarea value={config.industry_context || ''} onChange={e => setConfig(c => ({ ...c, industry_context: e.target.value }))}
              style={textareaStyle} placeholder="e.g., 'We specialize in healthcare programs (PSW, Pharmacy Tech, Dental). Our students are often career changers aged 25-45. Many are newcomers to Canada.'" data-testid="industry-context" />
          </div>

          <div>
            <label style={labelStyle}>Expected Tone & Communication Style</label>
            <textarea value={config.tone_guidance || ''} onChange={e => setConfig(c => ({ ...c, tone_guidance: e.target.value }))}
              style={textareaStyle} placeholder="e.g., 'Warm but professional. Use the student's name. Avoid jargon. Ask open-ended questions about their goals.'" data-testid="tone-guidance" />
          </div>
        </div>
      </div>

      {/* Scoring Focus Areas */}
      <div className="bg-white rounded-lg p-6" style={{ border: '1px solid #DDE3EC' }}>
        <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47', marginBottom: '4px' }}>Scoring Focus Areas</h3>
        <p style={helpStyle}>These areas will be weighted more heavily in the analysis.</p>
        <div className="flex flex-wrap gap-2 mt-3">
          {(config.scoring_focus || []).map((f, i) => (
            <span key={i} style={{ ...tagStyle, backgroundColor: '#EEF1F6', color: '#1D2F47' }}>
              {f} <button onClick={() => removeFocus(i)} style={{ color: '#A21D23', marginLeft: '2px', cursor: 'pointer' }}>&times;</button>
            </span>
          ))}
        </div>
        <div className="flex gap-2 mt-3">
          <input value={newFocus} onChange={e => setNewFocus(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addFocus())}
            style={{ ...inputStyle, flex: 1 }} placeholder="e.g., OSAP compliance, empathy, program knowledge" data-testid="add-focus-input" />
          <button onClick={addFocus} style={{ backgroundColor: '#EEF1F6', color: '#1E426C', height: '42px', padding: '0 16px', borderRadius: '6px', fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', fontWeight: 600 }} data-testid="add-focus-btn">Add</button>
        </div>
      </div>

      {/* Additional Questions */}
      <div className="bg-white rounded-lg p-6" style={{ border: '1px solid #DDE3EC' }}>
        <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47', marginBottom: '4px' }}>Additional Evaluation Questions</h3>
        <p style={helpStyle}>Beyond the standard Q1-Q5, add custom questions the AI should score (ternary: 1.0 / 0.5 / 0.0).</p>
        <div className="space-y-2 mt-3">
          {(config.additional_questions || []).map((q, i) => (
            <div key={i} className="flex items-center gap-2 rounded-lg p-3" style={{ backgroundColor: '#EEF1F6' }}>
              <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '14px', fontWeight: 700, color: '#1E426C', minWidth: '28px' }}>Q{i + 6}</span>
              <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', color: '#2F2F2F', flex: 1 }}>{q}</span>
              <button onClick={() => removeQuestion(i)} style={{ color: '#A21D23', cursor: 'pointer' }}>&times;</button>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-3">
          <input value={newQuestion} onChange={e => setNewQuestion(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addQuestion())}
            style={{ ...inputStyle, flex: 1 }} placeholder="e.g., Did the advisor explain the refund policy?" data-testid="add-question-input" />
          <button onClick={addQuestion} style={{ backgroundColor: '#EEF1F6', color: '#1E426C', height: '42px', padding: '0 16px', borderRadius: '6px', fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', fontWeight: 600 }} data-testid="add-question-btn">Add</button>
        </div>
      </div>

      {/* Compliance & Exemptions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-white rounded-lg p-6" style={{ border: '1px solid #DDE3EC' }}>
          <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47', marginBottom: '12px' }}>Compliance Notes</h3>
          <textarea value={config.compliance_notes || ''} onChange={e => setConfig(c => ({ ...c, compliance_notes: e.target.value }))}
            style={textareaStyle} placeholder="e.g., 'OSAP eligibility must be stated as conditional. Advisors must never guarantee financial aid amounts.'" data-testid="compliance-notes" />
        </div>
        <div className="bg-white rounded-lg p-6" style={{ border: '1px solid #DDE3EC' }}>
          <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47', marginBottom: '12px' }}>Do Not Penalize For</h3>
          <textarea value={config.do_not_penalize || ''} onChange={e => setConfig(c => ({ ...c, do_not_penalize: e.target.value }))}
            style={textareaStyle} placeholder="e.g., 'Do not penalize for not offering a campus tour if the call is clearly a follow-up. Do not penalize for being brief if the student only asked a single question.'" data-testid="do-not-penalize" />
        </div>
      </div>

      {/* Per-Role Custom Instructions */}
      <div className="bg-white rounded-lg overflow-hidden" style={{ border: '1px solid #DDE3EC' }}>
        <div className="px-6 py-4" style={{ borderBottom: '1px solid #DDE3EC' }}>
          <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47', marginBottom: '4px' }}>Per-Role Custom Instructions</h3>
          <p style={helpStyle}>Add role-specific evaluation guidance that applies ONLY when analyzing calls from that role. These are layered on top of the global instructions above.</p>
        </div>
        {METHODOLOGY_ROLES.map(role => {
          const roleConfig = config[role.key] || { custom_instructions: '' };
          const isExpanded = expandedRole === role.key;
          const hasContent = !!(roleConfig.custom_instructions || '').trim();
          return (
            <div key={role.key} style={{ borderBottom: '1px solid #EEF1F6' }}>
              <button
                onClick={() => setExpandedRole(isExpanded ? null : role.key)}
                className="w-full px-6 py-3.5 flex items-center justify-between hover:bg-slate-50 transition-colors"
                data-testid={`role-method-toggle-${role.key}`}
              >
                <div className="flex items-center gap-3">
                  <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1D2F47' }}>{role.label}</span>
                  {hasContent && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#EEF1F6', color: '#1E426C' }}>Customized</span>
                  )}
                </div>
                {isExpanded ? <ChevronDown size={14} style={{ color: '#8B95A1' }} /> : <ChevronRight size={14} style={{ color: '#8B95A1' }} />}
              </button>
              {isExpanded && (
                <div className="px-6 pb-5">
                  <p style={{ ...helpStyle, marginBottom: '8px' }}>{role.desc}</p>
                  <textarea
                    value={roleConfig.custom_instructions || ''}
                    onChange={e => setConfig(c => ({ ...c, [role.key]: { ...roleConfig, custom_instructions: e.target.value } }))}
                    style={textareaStyle}
                    placeholder={`e.g., 'For ${role.label} evaluations, prioritize...'`}
                    data-testid={`role-method-input-${role.key}`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {config.updated_at && (
        <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1', textAlign: 'right' }}>
          Last updated: {new Date(config.updated_at).toLocaleString()} by {config.updated_by}
        </p>
      )}
    </div>
  );
};


/* ─── DOA Training Reports Tab ─── */
const TrainingReportsView = () => {
  const [reports, setReports] = useState([]);
  const [filters, setFilters] = useState({ campuses: [], doas: [], roles: [], advisors: [] });
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [campusFilter, setCampusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [advisorFilter, setAdvisorFilter] = useState('');

  const fetchReports = useCallback(async () => {
    try {
      const [rRes, fRes] = await Promise.all([
        fetch(`${API}/api/self-audit/training-reports`, { credentials: 'include' }),
        fetch(`${API}/api/self-audit/training-report-filters`, { credentials: 'include' }),
      ]);
      if (rRes.ok) setReports((await rRes.json()).reports || []);
      if (fRes.ok) setFilters(await fRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  // Poll for generating reports
  useEffect(() => {
    const generating = reports.some(r => r.status === 'generating');
    if (!generating) return;
    const interval = setInterval(fetchReports, 5000);
    return () => clearInterval(interval);
  }, [reports, fetchReports]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      let confirmMsg = 'Generate a DOA training report from the analyzed calls matching the selected filters?';
      try {
        const params = new URLSearchParams();
        if (campusFilter) params.set('campus', campusFilter);
        if (roleFilter) params.set('staff_role', roleFilter);
        if (advisorFilter) params.set('advisor_name', advisorFilter);
        const previewRes = await fetch(`${API}/api/self-audit/training-report-preview?${params}`, { credentials: 'include' });
        if (previewRes.ok) {
          const preview = await previewRes.json();
          if (preview.total_calls === 0) {
            alert('No analyzed calls match the selected filters.');
            setGenerating(false);
            return;
          }
          const scopeNote = preview.total_calls > preview.calls_used
            ? `${preview.calls_used} of ${preview.total_calls} matching calls (capped)`
            : `${preview.calls_used} call${preview.calls_used === 1 ? '' : 's'}`;
          confirmMsg = `This will generate a training report from ${scopeNote}, using an estimated ~${preview.estimated_tokens.toLocaleString()} tokens. Continue?`;
        }
      } catch (e) { console.error(e); }

      if (!window.confirm(confirmMsg)) { setGenerating(false); return; }

      const res = await fetch(`${API}/api/self-audit/training-report`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campus: campusFilter, staff_role: roleFilter, advisor_name: advisorFilter }),
      });
      if (res.ok) {
        await fetchReports();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to generate report');
      }
    } catch (e) { console.error(e); }
    finally { setGenerating(false); }
  };

  const loadReport = async (id) => {
    try {
      const res = await fetch(`${API}/api/self-audit/training-report/${id}`, { credentials: 'include' });
      if (res.ok) setSelectedReport(await res.json());
    } catch (e) { console.error(e); }
  };

  const selectStyle = { border: '1px solid #DDE3EC', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', fontFamily: 'Source Sans 3, sans-serif', color: '#2F2F2F', backgroundColor: 'white', outline: 'none' };

  if (loading) return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin" style={{ color: '#1E426C' }} /></div>;

  // Detail view
  if (selectedReport && selectedReport.report) {
    const r = selectedReport.report;
    return (
      <div className="space-y-5" data-testid="training-report-detail">
        <div className="flex items-center justify-between">
          <button onClick={() => setSelectedReport(null)} className="flex items-center gap-2 hover:opacity-70" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', fontWeight: 600, color: '#1E426C' }} data-testid="back-to-reports">
            <ChevronLeft size={14} /> Back to Reports
          </button>
          <div className="flex items-center gap-2">
            <span style={{ fontSize: '11px', color: '#8B95A1' }}>{selectedReport.upload_count} calls analyzed</span>
            {selectedReport.filters?.campus && <span style={{ fontSize: '10px', fontWeight: 700, color: '#1E426C', backgroundColor: '#EEF1F6', padding: '2px 8px', borderRadius: '999px', border: '1px solid #DDE3EC' }}>{selectedReport.filters.campus}</span>}
            {selectedReport.filters?.advisor_name && <span style={{ fontSize: '10px', fontWeight: 700, color: '#A21D23', backgroundColor: '#FEF2F2', padding: '2px 8px', borderRadius: '999px', border: '1px solid #FECACA' }}>{selectedReport.filters.advisor_name}</span>}
            <button onClick={async () => {
              try {
                const res = await fetch(`${API}/api/self-audit/pdf/training-report/${selectedReport.id}`, { credentials: 'include' });
                if (res.ok) { const blob = await res.blob(); const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `training_report_${selectedReport.id}.pdf`; a.click(); URL.revokeObjectURL(a.href); }
              } catch (e) { console.error(e); }
            }} style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', backgroundColor: '#A21D23', color: 'white', height: '34px', padding: '0 14px', borderRadius: '3px', display: 'flex', alignItems: 'center', gap: '5px' }} data-testid="download-training-pdf">
              <Download size={12} /> Download PDF
            </button>
          </div>
        </div>

        {/* Synopsis */}
        <div className="bg-white rounded-lg p-6" style={{ border: '1px solid #DDE3EC', borderTop: '3px solid #1E426C' }}>
          <div className="flex items-center gap-2 mb-3">
            <BookOpen size={16} style={{ color: '#1E426C' }} />
            <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '18px', fontWeight: 600, color: '#1D2F47' }}>Executive Synopsis</h3>
            {r.team_grade && <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '14px', fontWeight: 700, color: '#A21D23' }}>Team Grade: {r.team_grade}</span>}
          </div>
          <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', color: '#2F2F2F', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{r.synopsis}</p>
          {r.priority_areas?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {r.priority_areas.map((a, i) => <span key={i} style={{ fontSize: '11px', fontWeight: 700, color: '#A21D23', backgroundColor: '#FEF2F2', padding: '4px 12px', borderRadius: '999px', border: '1px solid #FECACA' }}>{a}</span>)}
            </div>
          )}
        </div>

        {/* Micro-Behavioral Coaching */}
        {r.micro_behavioral_coaching && (
          <div className="bg-white rounded-lg p-6" style={{ border: '1px solid #DDE3EC', borderTop: '3px solid #2980B9' }}>
            <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47', marginBottom: '12px' }}>Micro-Behavioral Coaching (MBC)</h3>
            {Object.entries(r.micro_behavioral_coaching).map(([key, mbc]) => (
              <div key={key} className="mb-4 pb-4" style={{ borderBottom: '1px solid #EEF1F6' }}>
                <h4 style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', fontWeight: 700, color: '#1E426C', marginBottom: '6px' }}>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h4>
                <p style={{ fontSize: '13px', color: '#2F2F2F', marginBottom: '6px' }}><strong>Finding:</strong> {mbc.finding}</p>
                <p style={{ fontSize: '13px', color: '#2F2F2F', marginBottom: '8px' }}><strong>Recommendation:</strong> {mbc.recommendation}</p>
                {mbc.examples?.map((ex, i) => (
                  <div key={i} className="rounded-lg p-3 mb-2" style={{ backgroundColor: '#EEF1F6' }}>
                    <p style={{ fontSize: '12px', fontWeight: 700, color: '#1E426C' }}>{ex.staff}</p>
                    <p style={{ fontSize: '12px', color: '#A21D23', fontStyle: 'italic' }}>Current: "{ex.quote}"</p>
                    <p style={{ fontSize: '12px', color: '#16a34a', fontWeight: 600 }}>Improved: "{ex.improved}"</p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Objection Handling Playbook */}
        {r.objection_handling?.length > 0 && (
          <div className="bg-white rounded-lg p-6" style={{ border: '1px solid #DDE3EC', borderTop: '3px solid #A21D23' }}>
            <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47', marginBottom: '12px' }}>Objection Handling Playbook</h3>
            {r.objection_handling.map((obj, i) => (
              <div key={i} className="mb-5 pb-4" style={{ borderBottom: '1px solid #EEF1F6' }}>
                <div className="flex items-center gap-2 mb-2">
                  <Target size={14} style={{ color: '#A21D23' }} />
                  <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', fontWeight: 700, color: '#1D2F47' }}>{obj.objection_type}</span>
                  <span style={{ fontSize: '11px', color: '#8B95A1' }}>{obj.frequency}</span>
                </div>
                {obj.current_response && (
                  <div className="rounded-lg p-3 mb-2" style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }}>
                    <p style={{ fontSize: '11px', fontWeight: 700, color: '#A21D23', marginBottom: '2px' }}>Current Response ({obj.current_response.staff}):</p>
                    <p style={{ fontSize: '12px', color: '#2F2F2F', fontStyle: 'italic' }}>"{obj.current_response.quote}"</p>
                  </div>
                )}
                {obj.recommended_response && (
                  <div className="rounded-lg p-3 space-y-2" style={{ backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                    <p style={{ fontSize: '11px', fontWeight: 700, color: '#16a34a' }}>Hook & Pivot Framework:</p>
                    <p style={{ fontSize: '12px', color: '#2F2F2F' }}><strong style={{ color: '#1E426C' }}>Validate:</strong> "{obj.recommended_response.validate}"</p>
                    <p style={{ fontSize: '12px', color: '#2F2F2F' }}><strong style={{ color: '#1E426C' }}>Pivot:</strong> "{obj.recommended_response.pivot}"</p>
                    <p style={{ fontSize: '12px', color: '#2F2F2F' }}><strong style={{ color: '#1E426C' }}>Hook Question:</strong> "{obj.recommended_response.hook_question}"</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* DOA Coaching Highlights */}
        {r.doa_coaching_highlights?.length > 0 && (
          <div className="bg-white rounded-lg p-6" style={{ border: '1px solid #DDE3EC', borderTop: '3px solid #16a34a' }}>
            <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47', marginBottom: '12px' }}>DOA Coaching Highlights</h3>
            {r.doa_coaching_highlights.map((h, i) => (
              <div key={i} className="mb-4 pb-4" style={{ borderBottom: '1px solid #EEF1F6' }}>
                <h4 style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', fontWeight: 700, color: '#16a34a', marginBottom: '4px' }}>{h.title}</h4>
                <p style={{ fontSize: '13px', color: '#2F2F2F', lineHeight: 1.6, marginBottom: '8px' }}>{h.description}</p>
                {h.example_script && (
                  <div className="rounded-lg p-3" style={{ backgroundColor: '#EEF1F6', border: '1px solid #DDE3EC' }}>
                    <p style={{ fontSize: '11px', fontWeight: 700, color: '#8B95A1', marginBottom: '4px' }}>Example Script:</p>
                    <p style={{ fontSize: '13px', color: '#2F2F2F', fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>"{h.example_script}"</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Action Plan */}
        {r.action_plan && (
          <div className="bg-white rounded-lg p-6" style={{ border: '1px solid #DDE3EC' }}>
            <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47', marginBottom: '12px' }}>Action Plan</h3>
            <div className="grid grid-cols-3 gap-4">
              {[['week_1', 'Week 1', '#1E426C'], ['week_2', 'Week 2', '#2980B9'], ['day_30', '30-Day', '#A21D23']].map(([key, label, color]) => (
                <div key={key} className="rounded-lg p-4" style={{ backgroundColor: '#FAFBFC', borderTop: `3px solid ${color}` }}>
                  <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '14px', fontWeight: 700, color, marginBottom: '8px' }}>{label}</p>
                  <ul className="space-y-2">
                    {(r.action_plan[key] || []).map((a, i) => (
                      <li key={i} style={{ fontSize: '12px', color: '#2F2F2F', lineHeight: 1.5, paddingLeft: '12px', borderLeft: `2px solid ${color}` }}>{a}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Staff Benchmarks */}
        {r.staff_benchmarks?.length > 0 && (
          <div className="bg-white rounded-lg p-6" style={{ border: '1px solid #DDE3EC' }}>
            <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47', marginBottom: '12px' }}>Staff Benchmarks</h3>
            <div className="space-y-2">
              {r.staff_benchmarks.map((sb, i) => (
                <div key={i} className="flex items-center gap-4 px-4 py-3 rounded-lg" style={{ backgroundColor: '#FAFBFC', border: '1px solid #EEF1F6' }}>
                  <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1D2F47', width: '140px' }}>{sb.name}</span>
                  <span style={{ fontSize: '11px', color: '#8B95A1', width: '100px' }}>{sb.role}</span>
                  <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '14px', fontWeight: 700, color: '#1E426C', width: '60px' }}>{sb.avg_score}/10</span>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: String(sb.vs_team || '').startsWith('+') ? '#16a34a' : '#A21D23', width: '60px' }}>{sb.vs_team}</span>
                  <span style={{ fontSize: '12px', color: '#16a34a', flex: 1 }}>{sb.top_strength}</span>
                  <span style={{ fontSize: '12px', color: '#A21D23', flex: 1 }}>{sb.top_gap}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-5" data-testid="training-reports">
      {/* Generator */}
      <div className="bg-white rounded-lg p-6" style={{ border: '1px solid #DDE3EC', borderTop: '3px solid #1E426C' }}>
        <div className="flex items-center gap-2 mb-4">
          <GraduationCap size={18} style={{ color: '#1E426C' }} />
          <h2 style={{ fontFamily: 'Jost, sans-serif', fontSize: '18px', fontWeight: 600, color: '#1D2F47' }}>Generate DOA Training Report</h2>
        </div>
        <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', color: '#8B95A1', marginBottom: '16px' }}>
          AI-powered training reports with micro-behavioral coaching, objection handling playbooks, and real examples from your analyzed calls
        </p>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div>
            <label style={{ fontSize: '10px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Campus</label>
            <select value={campusFilter} onChange={e => setCampusFilter(e.target.value)} style={selectStyle} className="w-full" data-testid="training-campus-filter">
              <option value="">All Campuses</option>
              {['Toronto', 'North York', 'Mississauga', 'Brampton', 'Hamilton', 'Brantford', 'Kitchener', 'London', 'Windsor', 'Online'].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '10px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Staff Role</label>
            <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} style={selectStyle} className="w-full" data-testid="training-role-filter">
              <option value="">All Roles</option>
              <option value="admissions_advisor">Admissions Advisor</option>
              <option value="financial_aid_officer">Financial Aid Officer</option>
              <option value="qualifiers">Qualifiers</option>
              <option value="student_services">Student Services</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: '10px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Staff Member</label>
            <select value={advisorFilter} onChange={e => setAdvisorFilter(e.target.value)} style={selectStyle} className="w-full" data-testid="training-advisor-filter">
              <option value="">All Staff</option>
              {(filters.advisors || []).map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        </div>
        <button onClick={handleGenerate} disabled={generating}
          style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', backgroundColor: '#A21D23', color: 'white', height: '40px', padding: '0 24px', borderRadius: '3px', display: 'flex', alignItems: 'center', gap: '6px', opacity: generating ? 0.5 : 1 }}
          data-testid="generate-training-report-btn">
          {generating ? <Loader2 size={14} className="animate-spin" /> : <GraduationCap size={14} />}
          {generating ? 'Generating Report...' : 'Generate Training Report'}
        </button>
      </div>

      {/* Report List */}
      {reports.length > 0 && (
        <div className="bg-white rounded-lg overflow-hidden" style={{ border: '1px solid #DDE3EC' }}>
          <div className="px-6 py-3" style={{ backgroundColor: '#FAFBFC', borderBottom: '1px solid #DDE3EC' }}>
            <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Previous Reports ({reports.length})</span>
          </div>
          {reports.map(report => (
            <div key={report.id} className="px-6 py-4 flex items-center justify-between hover:bg-[#EEF1F6]/30 transition-colors cursor-pointer" style={{ borderBottom: '1px solid #EEF1F6' }}
              onClick={() => report.status === 'completed' && loadReport(report.id)} data-testid={`report-row-${report.id}`}>
              <div className="flex items-center gap-3">
                <GraduationCap size={16} style={{ color: report.status === 'completed' ? '#1E426C' : report.status === 'generating' ? '#92400E' : '#A21D23' }} />
                <div>
                  <div className="flex items-center gap-2">
                    <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1D2F47' }}>
                      Training Report — {report.upload_count} calls
                    </span>
                    <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px',
                      backgroundColor: report.status === 'completed' ? '#EEF1F6' : report.status === 'generating' ? '#FEF3C7' : '#FEF2F2',
                      color: report.status === 'completed' ? '#1E426C' : report.status === 'generating' ? '#92400E' : '#A21D23',
                      border: `1px solid ${report.status === 'completed' ? '#DDE3EC' : report.status === 'generating' ? '#FDE68A' : '#FECACA'}`,
                    }}>
                      {report.status === 'generating' ? 'Generating...' : report.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span style={{ fontSize: '11px', color: '#8B95A1' }}>{new Date(report.created_at).toLocaleString()}</span>
                    {report.filters?.campus && <span style={{ fontSize: '10px', color: '#1E426C', backgroundColor: '#EEF1F6', padding: '1px 6px', borderRadius: '999px' }}>{report.filters.campus}</span>}
                    {report.filters?.staff_role && <span style={{ fontSize: '10px', color: '#2980B9', backgroundColor: '#EBF5FB', padding: '1px 6px', borderRadius: '999px' }}>{report.filters.staff_role}</span>}
                    {report.filters?.advisor_name && <span style={{ fontSize: '10px', color: '#A21D23', backgroundColor: '#FEF2F2', padding: '1px 6px', borderRadius: '999px' }}>{report.filters.advisor_name}</span>}
                  </div>
                </div>
              </div>
              {report.status === 'generating' && <Loader2 size={16} className="animate-spin" style={{ color: '#92400E' }} />}
              {report.status === 'failed' && <span style={{ fontSize: '11px', color: '#A21D23', maxWidth: '200px' }} className="truncate">{report.error || 'Generation failed'}</span>}
              {report.status === 'completed' && <ChevronRight size={16} style={{ color: '#8B95A1' }} />}
            </div>
          ))}
        </div>
      )}

      {reports.length === 0 && (
        <div className="bg-white rounded-lg p-12 text-center" style={{ border: '1px solid #DDE3EC' }}>
          <GraduationCap size={28} className="mx-auto mb-3" style={{ color: '#DDE3EC' }} />
          <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47' }}>No training reports yet</p>
          <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', color: '#8B95A1', marginTop: '4px' }}>Generate your first DOA training report from analyzed calls above</p>
        </div>
      )}
    </div>
  );
};


/* ─── Color Field (must be outside BrandingSettingsView to avoid remount) ─── */
const ColorField = ({ label, value, field, onChange, help }) => (
  <div>
    <label style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', fontWeight: 600, color: '#2F2F2F', display: 'block', marginBottom: '6px' }}>{label}</label>
    <div className="flex items-center gap-2">
      <input type="color" value={value || '#000000'} onChange={e => onChange(field, e.target.value)}
        className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer p-0.5" style={{ backgroundColor: '#fff' }}
        data-testid={`branding-color-${field}`} />
      <input value={value || ''} onChange={e => onChange(field, e.target.value)}
        style={{ border: '1px solid #DDE3EC', borderRadius: '6px', padding: '10px 14px', fontSize: '13px', fontFamily: 'monospace', color: '#2F2F2F', flex: 1, outline: 'none' }}
        placeholder="#1E426C" data-testid={`branding-hex-${field}`} />
    </div>
    {help && <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1', marginTop: '4px' }}>{help}</p>}
  </div>
);

/* ─── Branding Settings (Admin Only) ─── */
const BrandingSettingsView = () => {
  const { config: current, updateConfig, fetchConfig } = useTenantConfig();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    setForm({
      brand_name: current.brand_name || '',
      logo_url: current.logo_url || '',
      primary_color: current.primary_color || '#1E426C',
      secondary_color: current.secondary_color || '#A21D23',
      page_bg: current.page_bg || '#EEF1F6',
      nav_deep: current.nav_deep || '#1D2F47',
      border_color: current.border_color || '#DDE3EC',
      text_primary: current.text_primary || '#2F2F2F',
      text_secondary: current.text_secondary || '#5B6572',
      text_muted: current.text_muted || '#8B95A1',
      font_heading: current.font_heading || 'Jost',
      font_body: current.font_body || 'Source Sans 3',
      email_placeholder: current.email_placeholder || '',
      footer_text: current.footer_text || '',
    });
  }, [current]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updateConfig(form);
      if (result) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
        await fetchConfig();
      } else {
        alert('Save failed — admin access required');
      }
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const inputStyle = { border: '1px solid #DDE3EC', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', fontFamily: 'Source Sans 3, sans-serif', color: '#2F2F2F', width: '100%', outline: 'none' };
  const labelStyle = { fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', fontWeight: 600, color: '#2F2F2F', display: 'block', marginBottom: '6px' };
  const helpStyle = { fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1', marginTop: '4px' };

  return (
    <div className="space-y-6" data-testid="branding-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 style={{ fontFamily: 'Jost, sans-serif', fontSize: '20px', fontWeight: 600, color: '#1D2F47' }}>Portal Branding</h2>
          <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', color: '#8B95A1' }}>
            Customize how your self-audit portal looks — logo, colors, and fonts for your organization
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setPreview(p => !p)}
            style={{ backgroundColor: preview ? '#1E426C' : '#EEF1F6', color: preview ? 'white' : '#5B6572', height: '38px', padding: '0 16px', borderRadius: '3px', fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', border: preview ? 'none' : '1px solid #DDE3EC' }}
            className="transition-all" data-testid="branding-preview-toggle">
            <Eye size={13} /> {preview ? 'Hide Preview' : 'Preview'}
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ backgroundColor: saved ? '#1E426C' : '#A21D23', color: 'white', height: '38px', padding: '0 24px', borderRadius: '3px', fontFamily: 'Source Sans 3, sans-serif', fontSize: '13.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}
            className="hover:opacity-90 transition-all disabled:opacity-50" data-testid="save-branding-btn">
            {saving ? <Loader2 size={13} className="animate-spin" /> : saved ? <Check size={13} /> : <Palette size={13} />}
            {saving ? 'Saving...' : saved ? 'Saved' : 'Save Branding'}
          </button>
        </div>
      </div>

      {/* Live Preview */}
      {preview && (
        <div className="rounded-lg overflow-hidden" style={{ border: '2px dashed #DDE3EC' }} data-testid="branding-preview">
          <div style={{ backgroundColor: form.primary_color, height: '4px' }} />
          <div className="bg-white px-6 py-3 flex items-center gap-3" style={{ borderBottom: `1px solid ${form.border_color}` }}>
            {form.logo_url && <img src={form.logo_url} alt="" className="h-8 w-8 object-contain" onError={e => e.target.style.display = 'none'} />}
            <span style={{ fontFamily: `${form.font_heading}, sans-serif`, fontSize: '16px', fontWeight: 600, color: form.nav_deep }}>{form.brand_name || 'Your Brand'}</span>
            <span style={{ fontFamily: `${form.font_body}, sans-serif`, fontSize: '12px', fontWeight: 700, color: form.secondary_color, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Self-Audit</span>
          </div>
          <div style={{ backgroundColor: form.page_bg, padding: '20px 24px' }}>
            <div className="flex items-center gap-3">
              <div style={{ backgroundColor: form.secondary_color, color: 'white', height: '32px', padding: '0 14px', borderRadius: '3px', fontFamily: `${form.font_body}, sans-serif`, fontSize: '12px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center' }}>Active Tab</div>
              <div style={{ backgroundColor: 'white', color: form.text_secondary, height: '32px', padding: '0 14px', borderRadius: '6px', fontFamily: `${form.font_body}, sans-serif`, fontSize: '13px', display: 'inline-flex', alignItems: 'center', border: `1px solid ${form.border_color}` }}>Other Tab</div>
            </div>
            <p style={{ fontFamily: `${form.font_body}, sans-serif`, fontSize: '13px', color: form.text_muted, marginTop: '12px' }}>{form.footer_text || 'Footer text preview'}</p>
          </div>
        </div>
      )}

      {/* Identity */}
      <div className="bg-white rounded-lg p-6" style={{ border: '1px solid #DDE3EC', borderTop: '3px solid #A21D23' }}>
        <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47', marginBottom: '16px' }}>Identity</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label style={labelStyle}>Organization Name</label>
            <input value={form.brand_name || ''} onChange={e => set('brand_name', e.target.value)} style={inputStyle}
              placeholder="Anderson College" data-testid="branding-brand-name" />
            <p style={helpStyle}>Displayed in the header and login page</p>
          </div>
          <div>
            <label style={labelStyle}>Logo URL</label>
            <div className="flex items-center gap-3">
              <input value={form.logo_url || ''} onChange={e => set('logo_url', e.target.value)} style={{ ...inputStyle, flex: 1 }}
                placeholder="https://example.com/logo.png" data-testid="branding-logo-url" />
              {form.logo_url && <img src={form.logo_url} alt="Preview" className="h-10 w-10 object-contain rounded border border-slate-200 bg-white p-0.5" onError={e => e.target.style.display = 'none'} />}
            </div>
            <p style={helpStyle}>Square image recommended (favicon or shield icon)</p>
          </div>
          <div>
            <label style={labelStyle}>Email Placeholder</label>
            <input value={form.email_placeholder || ''} onChange={e => set('email_placeholder', e.target.value)} style={inputStyle}
              placeholder="you@andersoncollege.com" data-testid="branding-email-placeholder" />
            <p style={helpStyle}>Hint text on the login email field</p>
          </div>
          <div>
            <label style={labelStyle}>Footer Text</label>
            <input value={form.footer_text || ''} onChange={e => set('footer_text', e.target.value)} style={inputStyle}
              placeholder="Anderson College Quality Assurance" data-testid="branding-footer-text" />
            <p style={helpStyle}>Shown below the login form</p>
          </div>
        </div>
      </div>

      {/* Color Palette */}
      <div className="bg-white rounded-lg p-6" style={{ border: '1px solid #DDE3EC' }}>
        <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47', marginBottom: '16px' }}>Color Palette</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <ColorField label="Primary Color" field="primary_color" value={form.primary_color} onChange={set} help="Header bar, buttons, active states" />
          <ColorField label="Accent Color" field="secondary_color" value={form.secondary_color} onChange={set} help="Tab highlights, CTA buttons, emphasis" />
          <ColorField label="Page Background" field="page_bg" value={form.page_bg} onChange={set} help="Main background tint" />
          <ColorField label="Header Text" field="nav_deep" value={form.nav_deep} onChange={set} help="Brand name and headings" />
          <ColorField label="Border Color" field="border_color" value={form.border_color} onChange={set} help="Card borders, dividers" />
          <ColorField label="Body Text" field="text_primary" value={form.text_primary} onChange={set} help="Primary content text" />
          <ColorField label="Secondary Text" field="text_secondary" value={form.text_secondary} onChange={set} help="Labels and supporting text" />
          <ColorField label="Muted Text" field="text_muted" value={form.text_muted} onChange={set} help="Hints and timestamps" />
        </div>
      </div>

      {/* Typography */}
      <div className="bg-white rounded-lg p-6" style={{ border: '1px solid #DDE3EC' }}>
        <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47', marginBottom: '16px' }}>Typography</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label style={labelStyle}>Heading Font</label>
            <input value={form.font_heading || ''} onChange={e => set('font_heading', e.target.value)} style={inputStyle}
              placeholder="Jost" data-testid="branding-font-heading" />
            <p style={helpStyle}>Google Fonts name for headings and KPI numbers</p>
          </div>
          <div>
            <label style={labelStyle}>Body Font</label>
            <input value={form.font_body || ''} onChange={e => set('font_body', e.target.value)} style={inputStyle}
              placeholder="Source Sans 3" data-testid="branding-font-body" />
            <p style={helpStyle}>Google Fonts name for body text and labels</p>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Main Dashboard ─── */
/* ─── Performance Insights Tab ─── */
const GRADE_COLORS = { A: '#16a34a', B: '#2980B9', C: '#CA8A04', D: '#EA580C', F: '#A21D23' };
const DIM_LABELS = {
  overall: 'Overall', tone_empathy: 'Tone & Empathy', information_accuracy: 'Information Accuracy',
  compliance: 'Compliance', consultative_skill: 'Consultative Skill', closing_ability: 'Closing Ability',
  persistence: 'Persistence',
};

const InsightsView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/self-audit/insights`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const openAdvisor = useCallback(async (name) => {
    setDetail({ advisor: name });
    setDetailLoading(true);
    try {
      const res = await fetch(`${API}/api/self-audit/advisor-detail?advisor_name=${encodeURIComponent(name)}`, { credentials: 'include' });
      if (res.ok) setDetail(await res.json());
      else setDetail(null);
    } catch (e) { console.error(e); setDetail(null); }
    finally { setDetailLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin" size={24} style={{ color: '#8B95A1' }} /></div>;
  }

  if (!data || data.total_analyzed === 0) {
    return (
      <div className="bg-white rounded-lg p-12 text-center" style={{ border: '1px solid #DDE3EC' }} data-testid="insights-empty">
        <Sparkles size={28} className="mx-auto mb-2" style={{ color: '#DDE3EC' }} />
        <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '18px', fontWeight: 600, color: '#1D2F47' }}>No insights yet</p>
        <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', color: '#8B95A1', marginTop: '4px' }}>Analyze some calls to unlock tenant-wide trends</p>
      </div>
    );
  }

  const grades = ['A', 'B', 'C', 'D', 'F'];
  const maxGrade = Math.max(...grades.map(g => data.grade_distribution[g] || 0), 1);
  const maxRisk = Math.max(...(data.compliance_risks || []).map(r => r.count), 1);
  const maxTrend = Math.max(...(data.trend || []).map(t => t.avg_overall || 0), 10);
  const topAdvisor = (data.by_advisor || [])[0];

  const Card = ({ children, testid }) => (
    <div className="bg-white rounded-lg p-5" style={{ border: '1px solid #DDE3EC' }} data-testid={testid}>{children}</div>
  );
  const SectionTitle = ({ icon: Icon, children }) => (
    <div className="flex items-center gap-2 mb-4">
      <Icon size={16} style={{ color: '#A21D23' }} />
      <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1D2F47' }}>{children}</h3>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="insights-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 style={{ fontFamily: 'Jost, sans-serif', fontSize: '20px', fontWeight: 600, color: '#1D2F47' }}>Performance Insights</h2>
          <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '14px', color: '#8B95A1', marginTop: '2px' }}>Tenant-wide trends across {data.total_analyzed} analyzed call{data.total_analyzed !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={fetchData} className="flex items-center gap-1.5 hover:opacity-70 transition-opacity" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', fontWeight: 600, color: '#1E426C' }} data-testid="insights-refresh">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg p-4" style={{ border: '1px solid #DDE3EC', borderTop: '3px solid #1E426C' }} data-testid="insight-total">
          <div className="flex items-center gap-1.5 mb-1"><BarChart3 size={13} style={{ color: '#1E426C' }} /><span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '11px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Calls Analyzed</span></div>
          <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '30px', fontWeight: 700, color: '#1D2F47', lineHeight: 1 }}>{data.total_analyzed}</p>
        </div>
        <div className="bg-white rounded-lg p-4" style={{ border: '1px solid #DDE3EC', borderTop: '3px solid #2980B9' }} data-testid="insight-avg">
          <div className="flex items-center gap-1.5 mb-1"><Gauge size={13} style={{ color: '#2980B9' }} /><span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '11px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Avg Score</span></div>
          <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '30px', fontWeight: 700, color: '#1D2F47', lineHeight: 1 }}>{data.avg_overall ?? '—'}<span style={{ fontSize: '14px', color: '#8B95A1', fontWeight: 500 }}>/10</span></p>
        </div>
        <div className="bg-white rounded-lg p-4" style={{ border: '1px solid #DDE3EC', borderTop: '3px solid #16a34a' }} data-testid="insight-top-advisor">
          <div className="flex items-center gap-1.5 mb-1"><Trophy size={13} style={{ color: '#16a34a' }} /><span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '11px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Top Advisor</span></div>
          <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '18px', fontWeight: 700, color: '#1D2F47', lineHeight: 1.1, marginTop: '4px' }}>{topAdvisor?.advisor || '—'}</p>
          <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1' }}>{topAdvisor ? `${topAdvisor.avg_overall}/10 · ${topAdvisor.calls} calls` : ''}</p>
        </div>
        <div className="bg-white rounded-lg p-4" style={{ border: '1px solid #DDE3EC', borderTop: '3px solid #A21D23' }} data-testid="insight-risks">
          <div className="flex items-center gap-1.5 mb-1"><ShieldAlert size={13} style={{ color: '#A21D23' }} /><span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '11px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Risk Flags</span></div>
          <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '30px', fontWeight: 700, color: '#1D2F47', lineHeight: 1 }}>{(data.compliance_risks || []).reduce((s, r) => s + r.count, 0)}</p>
        </div>
      </div>

      {/* Risk Alerts */}
      {(data.alerts || []).length > 0 && (
        <div className="bg-white rounded-lg p-5" style={{ border: '1px solid #FECACA', borderLeft: '4px solid #A21D23' }} data-testid="insight-alerts">
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert size={17} style={{ color: '#A21D23' }} />
            <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '16px', fontWeight: 600, color: '#A21D23' }}>Risk Alerts</h3>
            <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '12px', fontWeight: 700, color: 'white', backgroundColor: '#A21D23', padding: '1px 9px', borderRadius: '999px' }}>{data.alerts.length}</span>
          </div>
          <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', color: '#8B95A1', marginBottom: '12px' }}>Advisors whose compliance risks or scores crossed a threshold. Click to review their calls.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {data.alerts.map((al, i) => (
              <button key={i} onClick={() => openAdvisor(al.advisor)} className="text-left rounded-lg p-3 transition-all hover:shadow-md" style={{ border: `1px solid ${al.level === 'high' ? '#FECACA' : '#FED7AA'}`, backgroundColor: al.level === 'high' ? '#FEF2F2' : '#FFF7ED' }} data-testid={`alert-${i}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '14px', fontWeight: 700, color: '#1D2F47' }}>{al.advisor}</span>
                  <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'white', backgroundColor: al.level === 'high' ? '#A21D23' : '#EA580C', padding: '2px 8px', borderRadius: '999px' }}>{al.level}</span>
                </div>
                <ul className="space-y-0.5">
                  {al.reasons.slice(0, 2).map((r, j) => (
                    <li key={j} className="flex items-start gap-1.5" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#5B6572' }}>
                      <AlertTriangle size={11} style={{ color: al.level === 'high' ? '#A21D23' : '#EA580C', marginTop: '2px', flexShrink: 0 }} /> {r}
                    </li>
                  ))}
                </ul>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card testid="insight-grade-dist">
          <SectionTitle icon={Award}>Grade Distribution</SectionTitle>
          <div className="flex items-end justify-between gap-3 h-40 px-2">
            {grades.map(g => {
              const count = data.grade_distribution[g] || 0;
              const h = Math.round((count / maxGrade) * 100);
              return (
                <div key={g} className="flex-1 flex flex-col items-center justify-end h-full" data-testid={`grade-bar-${g}`}>
                  <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 700, color: '#5B6572', marginBottom: '4px' }}>{count}</span>
                  <div className="w-full rounded-t" style={{ height: `${Math.max(h, 3)}%`, backgroundColor: GRADE_COLORS[g], transition: 'height 500ms ease' }} />
                  <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '15px', fontWeight: 700, color: GRADE_COLORS[g], marginTop: '6px' }}>{g}</span>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Dimension averages */}
        <Card testid="insight-dimensions">
          <SectionTitle icon={Target}>Score by Dimension</SectionTitle>
          <div className="space-y-2.5">
            {Object.entries(DIM_LABELS).filter(([k]) => k !== 'overall').map(([key, label]) => {
              const v = data.dimension_averages[key];
              return (
                <div key={key} data-testid={`dim-${key}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', color: '#5B6572' }}>{label}</span>
                    <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 700, color: '#1D2F47' }}>{v ?? '—'}</span>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#EEF1F6' }}>
                    <div className="h-full rounded-full" style={{ width: `${((v || 0) / 10) * 100}%`, backgroundColor: v >= 7 ? '#16a34a' : v >= 5 ? '#CA8A04' : '#A21D23', transition: 'width 500ms ease' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Compliance risks */}
        <Card testid="insight-compliance">
          <SectionTitle icon={AlertTriangle}>Top Compliance Risks</SectionTitle>
          {(data.compliance_risks || []).length === 0 ? (
            <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', color: '#8B95A1' }}>No compliance risks flagged.</p>
          ) : (
            <div className="space-y-2">
              {data.compliance_risks.map((r, i) => (
                <div key={i} className="flex items-center gap-3" data-testid={`risk-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="truncate" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', color: '#2F2F2F' }}>{r.tag}</span>
                      <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '12px', fontWeight: 700, color: '#A21D23', marginLeft: '8px' }}>{r.count}</span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#FEF2F2' }}>
                      <div className="h-full rounded-full" style={{ width: `${(r.count / maxRisk) * 100}%`, backgroundColor: '#A21D23', transition: 'width 500ms ease' }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {Object.keys(data.osap_compliance_distribution || {}).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4 pt-3" style={{ borderTop: '1px solid #EEF1F6' }}>
              {Object.entries(data.osap_compliance_distribution).map(([k, v]) => (
                <span key={k} style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '999px', letterSpacing: '0.04em', textTransform: 'uppercase', color: k.includes('CRITICAL') || k.includes('FAIL') ? '#A21D23' : k.includes('RISK') ? '#EA580C' : k.includes('COMPLIANT') ? '#16a34a' : '#5B6572', backgroundColor: k.includes('CRITICAL') || k.includes('FAIL') ? '#FEF2F2' : k.includes('RISK') ? '#FFF7ED' : k.includes('COMPLIANT') ? '#F0FDF4' : '#EEF1F6' }} data-testid={`osap-${k}`}>
                  {k.replace(/_/g, ' ')}: {v}
                </span>
              ))}
            </div>
          )}
        </Card>

        {/* Monthly trend */}
        <Card testid="insight-trend">
          <SectionTitle icon={TrendingUp}>Score Trend</SectionTitle>
          {(data.trend || []).length === 0 ? (
            <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', color: '#8B95A1' }}>Not enough dated data yet.</p>
          ) : (
            <div className="flex items-end justify-between gap-2 h-40 px-1">
              {data.trend.map((t, i) => {
                const h = Math.round(((t.avg_overall || 0) / maxTrend) * 100);
                return (
                  <div key={i} className="flex-1 flex flex-col items-center justify-end h-full" data-testid={`trend-${t.month}`}>
                    <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '11px', fontWeight: 700, color: '#5B6572', marginBottom: '4px' }}>{t.avg_overall}</span>
                    <div className="w-full rounded-t" style={{ height: `${Math.max(h, 3)}%`, backgroundColor: '#1E426C', transition: 'height 500ms ease' }} />
                    <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '10px', color: '#8B95A1', marginTop: '6px' }}>{t.month.slice(2)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Advisor leaderboard */}
      <Card testid="insight-advisor-table">
        <SectionTitle icon={Users}>Advisor Leaderboard</SectionTitle>
        <div className="overflow-x-auto">
          <table className="w-full" style={{ fontFamily: 'Source Sans 3, sans-serif' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #DDE3EC' }}>
                <th className="text-left py-2" style={{ fontSize: '11px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Advisor</th>
                <th className="text-center py-2" style={{ fontSize: '11px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Calls</th>
                <th className="text-center py-2" style={{ fontSize: '11px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Grade</th>
                <th className="text-right py-2" style={{ fontSize: '11px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Avg Score</th>
              </tr>
            </thead>
            <tbody>
              {data.by_advisor.map((a, i) => (
                <tr key={i} onClick={() => openAdvisor(a.advisor)} className="cursor-pointer hover:bg-[#EEF1F6]/60 transition-colors" style={{ borderBottom: '1px solid #EEF1F6' }} data-testid={`advisor-row-${i}`}>
                  <td className="py-2.5" style={{ fontSize: '14px', fontWeight: 600, color: '#2F2F2F' }}>
                    <span className="inline-flex items-center gap-2">
                      {a.advisor}
                      {a.alert_level && <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '9px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'white', backgroundColor: a.alert_level === 'high' ? '#A21D23' : '#EA580C', padding: '1px 6px', borderRadius: '999px' }} data-testid={`advisor-risk-${i}`}>{a.alert_level === 'high' ? 'At Risk' : 'Watch'}</span>}
                    </span>
                  </td>
                  <td className="text-center py-2.5" style={{ fontSize: '13px', color: '#5B6572' }}>{a.calls}</td>
                  <td className="text-center py-2.5">
                    {a.grade && <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 700, color: 'white', backgroundColor: GRADE_COLORS[a.grade] || '#8B95A1', padding: '2px 9px', borderRadius: '4px' }}>{a.grade}</span>}
                  </td>
                  <td className="text-right py-2.5">
                    <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '15px', fontWeight: 700, color: '#1D2F47' }}>{a.avg_overall ?? '—'}</span>
                    <span style={{ fontSize: '11px', color: '#8B95A1' }}>/10</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Role + Campus */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card testid="insight-by-role">
          <SectionTitle icon={Shield}>By Role</SectionTitle>
          <div className="space-y-2">
            {data.by_role.map((r, i) => (
              <div key={i} className="flex items-center justify-between" data-testid={`role-row-${i}`}>
                <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', color: '#2F2F2F' }}>{r.role} <span style={{ color: '#8B95A1' }}>({r.calls})</span></span>
                <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '14px', fontWeight: 700, color: '#1D2F47' }}>{r.avg_overall ?? '—'}<span style={{ fontSize: '11px', color: '#8B95A1' }}>/10</span></span>
              </div>
            ))}
          </div>
        </Card>
        <Card testid="insight-by-campus">
          <SectionTitle icon={TrendingUp}>By Campus</SectionTitle>
          <div className="space-y-2">
            {data.by_campus.map((c, i) => (
              <div key={i} className="flex items-center justify-between" data-testid={`campus-row-${i}`}>
                <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', color: '#2F2F2F' }}>{c.campus} <span style={{ color: '#8B95A1' }}>({c.calls})</span></span>
                <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '14px', fontWeight: 700, color: '#1D2F47' }}>{c.avg_overall ?? '—'}<span style={{ fontSize: '11px', color: '#8B95A1' }}>/10</span></span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Advisor Drilldown Modal */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(29,47,71,0.55)' }} onClick={() => setDetail(null)} data-testid="advisor-drilldown-overlay">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[88vh] overflow-y-auto" style={{ border: '1px solid #DDE3EC' }} onClick={(e) => e.stopPropagation()} data-testid="advisor-drilldown">
            <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid #DDE3EC' }}>
              <div className="flex items-center gap-3">
                <div className="rounded-full flex items-center justify-center" style={{ width: '38px', height: '38px', backgroundColor: '#1E426C' }}>
                  <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '15px', fontWeight: 700, color: 'white' }}>{(detail.advisor || '?').charAt(0)}</span>
                </div>
                <div>
                  <h3 style={{ fontFamily: 'Jost, sans-serif', fontSize: '18px', fontWeight: 600, color: '#1D2F47' }} data-testid="drilldown-name">{detail.advisor}</h3>
                  {detail.total_calls != null && <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1' }}>{detail.total_calls} call{detail.total_calls !== 1 ? 's' : ''} · avg {detail.avg_overall ?? '—'}/10</p>}
                </div>
              </div>
              <button onClick={() => setDetail(null)} style={{ color: '#8B95A1' }} className="hover:opacity-70" data-testid="drilldown-close"><X size={20} /></button>
            </div>

            {detailLoading || detail.total_calls == null ? (
              <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin" size={24} style={{ color: '#8B95A1' }} /></div>
            ) : (
              <div className="p-6 space-y-5">
                {/* Grade chips + risks */}
                <div className="flex flex-wrap items-center gap-2">
                  {['A', 'B', 'C', 'D', 'F'].map(g => (detail.grade_distribution[g] > 0) && (
                    <span key={g} style={{ fontFamily: 'Jost, sans-serif', fontSize: '12px', fontWeight: 700, color: 'white', backgroundColor: GRADE_COLORS[g], padding: '3px 10px', borderRadius: '4px' }}>{g}: {detail.grade_distribution[g]}</span>
                  ))}
                  {(detail.top_risks || []).length > 0 && <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', fontWeight: 700, color: '#A21D23', backgroundColor: '#FEF2F2', padding: '3px 10px', borderRadius: '999px', border: '1px solid #FECACA' }}>{detail.top_risks.reduce((s, r) => s + r.count, 0)} risk flags</span>}
                </div>

                {/* Coaching notes */}
                <div>
                  <div className="flex items-center gap-2 mb-3"><BookOpen size={15} style={{ color: '#A21D23' }} /><h4 style={{ fontFamily: 'Jost, sans-serif', fontSize: '15px', fontWeight: 600, color: '#1D2F47' }}>Coaching Notes</h4></div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {[
                      { key: 'strengths', label: 'Strengths', color: '#16a34a', bg: '#F0FDF4' },
                      { key: 'focus_areas', label: 'Focus Areas', color: '#EA580C', bg: '#FFF7ED' },
                      { key: 'recommendations', label: 'Recommendations', color: '#1E426C', bg: '#EEF1F6' },
                    ].map(col => (
                      <div key={col.key} className="rounded-lg p-3" style={{ backgroundColor: col.bg }} data-testid={`coaching-${col.key}`}>
                        <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '11px', fontWeight: 700, color: col.color, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '8px' }}>{col.label}</p>
                        {(detail.coaching_notes[col.key] || []).length === 0 ? (
                          <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#8B95A1' }}>None recorded.</p>
                        ) : (
                          <ul className="space-y-1.5">
                            {detail.coaching_notes[col.key].map((n, i) => (
                              <li key={i} className="flex items-start gap-1.5" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12.5px', color: '#2F2F2F', lineHeight: 1.35 }}>
                                <span style={{ color: col.color, fontWeight: 700 }}>•</span>
                                <span>{n.text}{n.count > 1 && <span style={{ color: '#8B95A1' }}> ×{n.count}</span>}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Calls */}
                <div>
                  <div className="flex items-center gap-2 mb-3"><FileText size={15} style={{ color: '#A21D23' }} /><h4 style={{ fontFamily: 'Jost, sans-serif', fontSize: '15px', fontWeight: 600, color: '#1D2F47' }}>Calls ({detail.calls.length})</h4></div>
                  <div className="space-y-2.5">
                    {detail.calls.map((c, i) => (
                      <div key={i} className="rounded-lg p-3.5" style={{ border: '1px solid #DDE3EC' }} data-testid={`drilldown-call-${i}`}>
                        <div className="flex items-center justify-between gap-3 mb-1.5">
                          <div className="flex items-center gap-2 min-w-0">
                            {c.grade && <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '12px', fontWeight: 700, color: 'white', backgroundColor: GRADE_COLORS[c.grade] || '#8B95A1', padding: '2px 8px', borderRadius: '4px', flexShrink: 0 }}>{c.grade}</span>}
                            <span className="truncate" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '13px', fontWeight: 600, color: '#2F2F2F' }}>{c.call_type_label || 'Call'}</span>
                            {c.campus && <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '11px', color: '#8B95A1', flexShrink: 0 }}>· {c.campus}</span>}
                          </div>
                          <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '15px', fontWeight: 700, color: '#1D2F47', flexShrink: 0 }}>{c.overall ?? '—'}<span style={{ fontSize: '11px', color: '#8B95A1' }}>/10</span></span>
                        </div>
                        {c.summary && <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12.5px', color: '#5B6572', lineHeight: 1.4 }}>{c.summary}</p>}
                        {(c.risk_tags || []).length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {c.risk_tags.slice(0, 4).map((t, j) => (
                              <span key={j} style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '10.5px', fontWeight: 600, color: '#A21D23', backgroundColor: '#FEF2F2', padding: '2px 8px', borderRadius: '999px', border: '1px solid #FECACA' }}>{t}</span>
                            ))}
                          </div>
                        )}
                        {c.created_at && <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '11px', color: '#8B95A1', marginTop: '6px' }}>{new Date(c.created_at).toLocaleDateString()}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function SelfAuditDashboard() {
  const { user, logout } = useAuth();
  const { config: tc } = useTenantConfig();
  const [tab, setTab] = useState('audit');
  const [tokenUsage, setTokenUsage] = useState(null);
  const [teamRole, setTeamRole] = useState(null);
  const [showOverview, setShowOverview] = useState(false);

  const fetchTokenUsage = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/self-audit/token-usage`, { credentials: 'include' });
      if (res.ok) setTokenUsage(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchTeamRole = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/self-audit/my-role`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setTeamRole(d.team_role); }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchTokenUsage(); fetchTeamRole(); const i = setInterval(fetchTokenUsage, 30000); return () => clearInterval(i); }, [fetchTokenUsage, fetchTeamRole]);

  // DOA accounts are restricted to the Training module only
  useEffect(() => { if (teamRole === 'doa') setTab('training'); }, [teamRole]);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  const downloadPdf = async (url) => {
    try {
      const res = await fetch(`${API}${url}`, { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = url.split('/').pop() + '.pdf';
        a.click();
        URL.revokeObjectURL(a.href);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'PDF download failed');
      }
    } catch (e) { console.error(e); alert('Download failed'); }
  };

  const isAdmin = teamRole === 'admin';
  const isDoa = teamRole === 'doa';

  // Role scorecards for dashboard header
  const [roleScores, setRoleScores] = useState(null);
  useEffect(() => {
    const fetchRoleScores = async () => {
      try {
        const res = await fetch(`${API}/api/self-audit/role-performance`, { credentials: 'include' });
        if (res.ok) setRoleScores(await res.json());
      } catch (e) { console.warn(e); }
    };
    fetchRoleScores();
  }, [tab]);

  const ROLE_SC = {
    admissions_advisor: { color: '#1E426C', label: 'Admissions' },
    financial_aid_officer: { color: '#A21D23', label: 'FAO' },
    qualifiers: { color: '#2980B9', label: 'Qualifiers' },
    student_services: { color: '#16a34a', label: 'Student Svc' },
  };

  const tabs = isDoa ? [
    { id: 'training', icon: GraduationCap, label: 'Training' },
  ] : [
    { id: 'audit', icon: Upload, label: 'Upload' },
    { id: 'comparison', icon: Users, label: 'Staff' },
    { id: 'role_perf', icon: BarChart3, label: 'Roles' },
    { id: 'campus', icon: TrendingUp, label: 'Campus' },
    { id: 'insights', icon: Sparkles, label: 'Insights' },
    { id: 'training', icon: GraduationCap, label: 'Training' },
    ...(isAdmin ? [{ id: 'settings', icon: Shield, label: 'Methodology' }] : []),
    ...(isAdmin ? [{ id: 'branding', icon: Palette, label: 'Branding' }] : []),
    { id: 'activity', icon: Activity, label: 'Activity' },
    { id: 'tokens', icon: Clock, label: 'Tokens' },
    ...(isAdmin ? [{ id: 'team', icon: UserPlus, label: 'Team' }] : []),
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: tc.page_bg, fontFamily: `'${tc.font_body}', 'Source Sans Pro', sans-serif` }}>
      {/* Google Fonts */}
      <link href={`https://fonts.googleapis.com/css2?family=${tc.font_heading.replace(/ /g, '+')}:wght@500;600;700&family=${tc.font_body.replace(/ /g, '+')}:wght@400;500;600;700&display=swap`} rel="stylesheet" />

      {/* Navy Utility Strip */}
      <div style={{ backgroundColor: tc.primary_color, height: '4px' }} />

      {/* Header */}
      <header className="bg-white sticky top-0 z-50" style={{ borderBottom: `1px solid ${tc.border_color}` }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            {tc.logo_url && <img src={tc.logo_url} alt={tc.brand_name} className="h-9 w-9 object-contain" data-testid="header-logo" />}
            <div className="flex items-baseline gap-2">
              <span style={{ fontFamily: `${tc.font_heading}, sans-serif`, fontSize: '16px', fontWeight: 600, color: tc.nav_deep }} data-testid="header-brand-name">{tc.brand_name}</span>
              <span style={{ fontFamily: `${tc.font_body}, sans-serif`, fontSize: '12px', fontWeight: 700, color: tc.secondary_color, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Self-Audit</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {tokenUsage && (
              <div className="flex items-center gap-2 rounded px-3 py-1.5" style={{ backgroundColor: tc.page_bg, border: `1px solid ${tc.border_color}` }} data-testid="token-header-display">
                <Fuel size={12} style={{ color: tokenUsage.pct_used >= 90 ? tc.secondary_color : tc.primary_color }} />
                <span style={{ fontFamily: `${tc.font_body}, sans-serif`, fontSize: '12px', fontWeight: 600, color: tc.text_secondary }}>{tokenUsage.tokens_remaining.toLocaleString()} tokens left</span>
              </div>
            )}
            <button onClick={() => setShowOverview(true)} className="flex items-center gap-1.5 rounded px-3 py-1.5 hover:opacity-80 transition-opacity" style={{ backgroundColor: tc.primary_color, border: 'none', fontFamily: `${tc.font_body}, sans-serif`, fontSize: '12px', fontWeight: 700, color: 'white', letterSpacing: '0.06em', textTransform: 'uppercase' }} data-testid="overview-btn">
              <HelpCircle size={13} /> Overview
            </button>
            <button onClick={() => downloadPdf('/api/self-audit/pdf/methodology')} className="flex items-center gap-1.5 rounded px-3 py-1.5 hover:opacity-80 transition-opacity" style={{ backgroundColor: tc.page_bg, border: `1px solid ${tc.border_color}`, fontFamily: `${tc.font_body}, sans-serif`, fontSize: '12px', fontWeight: 600, color: tc.text_secondary }} data-testid="methodology-pdf-btn">
              <FileText size={12} /> Methodology
            </button>
            <span style={{ fontFamily: `${tc.font_body}, sans-serif`, fontSize: '14px', color: tc.text_secondary }}>{user?.name || user?.email}</span>
            <button onClick={handleLogout} className="hover:opacity-70 transition-opacity" style={{ color: tc.text_muted }} data-testid="logout-btn">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 space-y-4">
        {/* Token Meter + Role Scorecards */}
        {!isDoa && <div className="flex items-stretch gap-4">
          <div className="w-[220px] flex-shrink-0">
            <TokenMeter usage={tokenUsage} />
          </div>
          <div className="flex-1 grid grid-cols-4 gap-3" data-testid="role-scorecards">
            {['admissions_advisor', 'financial_aid_officer', 'qualifiers', 'student_services'].map(role => {
              const sc = ROLE_SC[role] || {};
              const s = roleScores?.summaries?.find(r => r.role === role);
              const hasCalls = s && s.call_count > 0;
              return (
                <div key={role} className="bg-white rounded-lg p-3.5 flex flex-col justify-between" style={{ border: '1px solid #DDE3EC', borderTop: `3px solid ${sc.color}` }} data-testid={`scorecard-${role}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '11px', fontWeight: 700, color: '#8B95A1', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{sc.label}</span>
                    {hasCalls && <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 700, color: sc.color }}>{s.grade}</span>}
                  </div>
                  {hasCalls ? (
                    <>
                      <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '28px', fontWeight: 700, color: '#1D2F47', lineHeight: 1 }}>{s.avg_overall}<span style={{ fontSize: '14px', color: '#8B95A1', fontWeight: 500 }}>/10</span></p>
                      <div className="mt-2 flex items-center justify-between">
                        <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '11px', color: '#8B95A1' }}>{s.call_count} call{s.call_count !== 1 ? 's' : ''}</span>
                        <div className="flex-1 mx-2 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#EEF1F6' }}>
                          <div className="h-full rounded-full" style={{ width: `${Math.min((s.avg_overall / 10) * 100, 100)}%`, backgroundColor: sc.color, transition: 'width 500ms ease' }} />
                        </div>
                        <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '11px', color: '#8B95A1' }}>{(s.avg_composite * 100).toFixed(0)}%</span>
                      </div>
                    </>
                  ) : (
                    <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: '12px', color: '#DDE3EC', marginTop: '4px' }}>No data</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>}

        {/* Tabs */}
        <div className="flex items-center justify-center gap-1.5 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className="flex items-center gap-1.5 px-3 transition-all whitespace-nowrap shrink-0"
                style={{
                  height: '36px',
                  borderRadius: tab === t.id ? '3px' : '6px',
                  fontFamily: `${tc.font_body}, sans-serif`,
                  fontSize: tab === t.id ? '12px' : '13px',
                  fontWeight: tab === t.id ? 700 : 500,
                  letterSpacing: tab === t.id ? '0.06em' : 'normal',
                  textTransform: tab === t.id ? 'uppercase' : 'none',
                  backgroundColor: tab === t.id ? tc.secondary_color : tc.surface_color,
                  color: tab === t.id ? 'white' : tc.text_secondary,
                  border: tab === t.id ? 'none' : `1px solid ${tc.border_color}`,
                }}
              >
                <t.icon size={13} /> {t.label}
              </button>
            ))}
          </div>

        {/* Tab Content */}
        {tab === 'audit' && <SelfAudit embeddedMode onDownloadPdf={(id) => downloadPdf(`/api/self-audit/pdf/analysis/${id}`)} onDownloadBatchPdf={(name) => downloadPdf(`/api/self-audit/pdf/batch/${encodeURIComponent(name)}`)} />}
        {tab === 'comparison' && <StaffComparisonView onDownloadPdf={() => downloadPdf('/api/self-audit/pdf/comparison')} />}
        {tab === 'role_perf' && <RolePerformanceView />}
        {tab === 'campus' && <CampusPerformanceView />}
        {tab === 'insights' && <InsightsView />}
        {tab === 'training' && <TrainingReportsView />}
        {tab === 'settings' && isAdmin && <MethodologySettingsView />}
        {tab === 'branding' && isAdmin && <BrandingSettingsView />}
        {tab === 'activity' && <ActivityLogView />}
        {tab === 'tokens' && <TokenHistoryView isAdmin={isAdmin} />}
        {tab === 'team' && isAdmin && <TeamManagementView />}
      </main>

      {/* Overview Modal */}
      {showOverview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(29,47,71,0.5)' }} onClick={() => setShowOverview(false)} data-testid="overview-modal-overlay">
          <div className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[85vh] overflow-hidden flex flex-col" style={{ border: `1px solid ${tc.border_color}`, boxShadow: '0 24px 64px -16px rgba(29,47,71,0.3)' }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="px-8 py-5 flex items-center justify-between flex-shrink-0" style={{ borderBottom: `1px solid ${tc.border_color}`, borderTop: `4px solid ${tc.secondary_color}` }}>
              <div className="flex items-center gap-3">
                {tc.logo_url && <img src={tc.logo_url} alt="" className="h-8 w-8 object-contain" />}
                <div>
                  <h2 style={{ fontFamily: `${tc.font_heading}, sans-serif`, fontSize: '20px', fontWeight: 600, color: tc.nav_deep }}>How to Use Self-Audit</h2>
                  <p style={{ fontFamily: `${tc.font_body}, sans-serif`, fontSize: '12px', color: tc.text_muted }}>A quick guide to evaluating your admissions team</p>
                </div>
              </div>
              <button onClick={() => setShowOverview(false)} style={{ color: tc.text_muted }} className="hover:opacity-70" data-testid="overview-close"><X size={20} /></button>
            </div>

            {/* Content */}
            <div className="px-8 py-6 overflow-y-auto flex-1 space-y-6" style={{ fontFamily: `${tc.font_body}, sans-serif` }}>
              {[
                { step: 1, title: 'Upload Recordings', color: tc.primary_color,
                  desc: 'Drag and drop call recordings (MP3, WAV, M4A) or SMS screenshots (PNG, JPG) into the upload area. Each file can be up to 25MB.',
                  tip: 'You can upload multiple files at once. The system will extract audio metadata automatically.' },
                { step: 2, title: 'Map to Advisor', color: '#2980B9',
                  desc: 'Assign each upload to the advisor who handled the call and label the call type (e.g., Consultative, Inbound Inquiry). This groups files for comparison.',
                  tip: 'Be consistent with advisor names — "Jun Yang" and "Jun" will be treated as different people.' },
                { step: 3, title: 'Transcribe & Analyze', color: tc.secondary_color,
                  desc: 'Click "Transcribe + Analyze" to process the file. Audio is transcribed by AI (Whisper), then the transcript is evaluated against 5 mandatory audit questions, 6 performance dimensions, and your custom methodology settings.',
                  tip: 'Analysis runs in the background — you can navigate away and come back. The dashboard polls for completion automatically.' },
                { step: 4, title: 'Review Results', color: '#16a34a',
                  desc: 'Each analyzed file shows a grade (A–F), overall score (out of 10), Q1–Q5 audit scores with evidence quotes, insight tags, and moment-of-truth highlights. Expand any card to see the full analysis.',
                  tip: 'Download individual PDFs for record-keeping. The PDF includes the full transcript, score reasoning, and source-of-truth verification.' },
                { step: 5, title: 'Give Feedback', color: '#9333EA',
                  desc: 'Click "Feedback" on any analyzed file to rate accuracy and flag disagreements. Your feedback is stored and automatically injected into future analyses — the AI learns from your corrections.',
                  tip: 'Be specific: "Q3 scored 0 but the advisor mentioned prerequisites at 5:30" helps the system calibrate.' },
                { step: 6, title: 'Compare Staff', color: '#D97706',
                  desc: 'Switch to the Staff Comparison tab to see a ranked leaderboard of all advisors. Scores are averaged across all analyzed calls. Download the comparison PDF for management reports.',
                  tip: 'Expand any advisor row to see their dimension-by-dimension breakdown and Q1–Q5 averages.' },
              ].map(s => (
                <div key={s.step} className="flex gap-4">
                  <div className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: s.color, color: 'white', fontFamily: `${tc.font_heading}, sans-serif`, fontSize: '16px', fontWeight: 700 }}>
                    {s.step}
                  </div>
                  <div className="flex-1">
                    <h3 style={{ fontFamily: `${tc.font_heading}, sans-serif`, fontSize: '16px', fontWeight: 600, color: tc.nav_deep, marginBottom: '4px' }}>{s.title}</h3>
                    <p style={{ fontSize: '14px', color: tc.text_primary, lineHeight: 1.5 }}>{s.desc}</p>
                    <div className="mt-2 rounded-md px-3 py-2" style={{ backgroundColor: tc.page_bg }}>
                      <p style={{ fontSize: '12px', color: tc.text_secondary }}><strong style={{ color: tc.primary_color }}>Tip:</strong> {s.tip}</p>
                    </div>
                  </div>
                </div>
              ))}

              {/* Admin-only section */}
              {isAdmin && (
                <div className="pt-4" style={{ borderTop: `1px solid ${tc.border_color}` }}>
                  <h3 style={{ fontFamily: `${tc.font_heading}, sans-serif`, fontSize: '16px', fontWeight: 600, color: tc.secondary_color, marginBottom: '8px' }}>Admin Features</h3>
                  <div className="space-y-3">
                    <p style={{ fontSize: '14px', color: tc.text_primary, lineHeight: 1.5 }}>
                      <strong>Methodology tab:</strong> Customize how the AI evaluates your team — add custom instructions, scoring focus areas, additional evaluation questions, compliance notes, and exemptions. Changes apply only to your organization.
                    </p>
                    <p style={{ fontSize: '14px', color: tc.text_primary, lineHeight: 1.5 }}>
                      <strong>Team tab:</strong> Invite colleagues as Admins (full access including Methodology) or Analysts (upload, analyze, compare, and give feedback — but cannot modify scoring settings).
                    </p>
                    <p style={{ fontSize: '14px', color: tc.text_primary, lineHeight: 1.5 }}>
                      <strong>Activity Log:</strong> See all actions across your team — uploads, analyses, feedback, and settings changes — with timestamps and user attribution.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-8 py-4 flex-shrink-0" style={{ borderTop: `1px solid ${tc.border_color}`, backgroundColor: '#FAFBFC' }}>
              <button onClick={() => setShowOverview(false)} style={{ backgroundColor: tc.secondary_color, color: 'white', height: '38px', padding: '0 24px', borderRadius: '3px', fontFamily: `${tc.font_body}, sans-serif`, fontSize: '13.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', width: '100%' }} data-testid="overview-got-it">
                Got It
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
